import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { isSchemaPending } from '../lib/supabase.js';
import DbPending from '../components/DbPending.jsx';
import * as db from '../lib/data.js';
import { Modal, Fld, CountrySelect, COMMON_COUNTRIES } from '../components/Modals.jsx';
import { hueFor, toRailVM } from '../lib/viewmodel.js';
import { reqLabel, reqCat } from '../lib/reqtypes.js';
import { DOC_HEAD } from '../lib/company.js';
import { clockText } from '../lib/today.js';
import { useSigner, SignerEditor } from '../components/Signer.jsx';
import PurchaseOrders from '../components/PurchaseOrders.jsx';

// แท็บ "แบรนด์" — ศูนย์ข้อมูลแบรนด์/บริษัทคู่ค้า (international brands coordinator ในกล่อง)
// 1 หน้า/แบรนด์ ตอบใน 60 วินาที: ติดต่อใคร · สั่งอย่างไร · ระวังอะไร · ต้องทำอะไรต่อ · เอกสาร/ตู้ทั้งหมดอยู่ไหน
// ไม่กรอกซ้ำ: เอกสาร PI/Invoice/PL/BL ดึงจากตู้ (inbound.document) · บทเรียนดึงจากสมุดบันทึกตู้ (v_watchouts)
// · เตือนความจำใช้ตาราง reminder ตัวเดียวกับแท็บ "วันนี้" (ขึ้นปฏิทิน+เสียงเตือนเหมือนเดิม)

const HUES = ['#8b5cf6', '#2f6bff', '#f0930c', '#12b76a', '#00b3c7', '#ff5470'];
const hueOf = (p) => p?.brand_hue || hueFor(p?.supplier_name || '');
const countryTh = (cc) => { const f = COMMON_COUNTRIES.find(([c]) => c === cc); return f ? `${f[1]} (${cc})` : (cc || ''); };

// ป้ายประเภท = สีกลางเดียว (สีในแอพนี้สงวนไว้บอกความหมาย: หมวดเอกสาร × ความเร่ง × สีประจำแบรนด์)
const NOTE_KINDS = [
  { id: 'meeting', t: 'ประชุม / นัดหมาย', c: '#565c72', hint: 'วาระ + สรุปผล + สิ่งที่ตกลงกัน' },
  { id: 'call', t: 'โทร / วิดีโอคอล', c: '#565c72', hint: 'คุยกับใคร เรื่องอะไร ได้ข้อสรุปว่า' },
  { id: 'email', t: 'อีเมล / แชท', c: '#565c72', hint: 'สรุปใจความจากอีเมล/แชทที่สำคัญ' },
  { id: 'note', t: 'บันทึก', c: '#565c72', hint: 'เรื่องที่ทีมต้องรู้' },
];
const kindOf = (id) => NOTE_KINDS.find(k => k.id === id) ?? NOTE_KINDS[3];

// ช่องเอกสารมาตรฐานของทุกตู้ — เรียงตามลำดับงานจริง (สั่ง → จ่าย → ส่ง → Form → พิธีการ)
// ทุกตู้มีผังเดียวกันเป๊ะ สายตาจึงจำได้ว่า "ช่อง ④ = Packing" เสมอ และช่องว่างจะเด้งขึ้นมาเอง
const SLOTS = [
  { s: 'PI',          t: ['proforma_invoice'] },
  { s: 'โอนเงิน',      t: ['payment_proof'] },
  { s: 'Invoice',     t: ['commercial_invoice'] },
  { s: 'Packing',     t: ['packing_list'] },
  { s: 'B/L · AWB',   t: ['bl', 'awb'] },
  { s: 'CO / Form',   t: ['certificate_of_origin'] },
  { s: 'ใบขน',        t: ['declaration'] },
  { s: 'อากร / VAT',  t: ['duty_receipt'] },
];
const SLOT_TYPES = new Set(SLOTS.flatMap(x => x.t));
const BLOCKING_BY = ['before_ship', 'before_pickup', 'before_clear'];
const SLOT_MARK = { ok: '✓', flag: '!', miss: '＋', na: '·' };
// แยกนามสกุลไฟล์ออก เพื่อไม่ให้ ".pdf" โดนตัดหายตอนชื่อยาว
const splitName = (n = '') => { const i = String(n).lastIndexOf('.'); return i > 0 ? [n.slice(0, i), n.slice(i)] : [String(n || 'ไฟล์'), '']; };
// เน้นคำที่ค้นเจอด้วยพื้นเทา (ไม่ใช้สี — สีในแอพนี้สงวนไว้บอกความหมาย)
const hiText = (t, q) => {
  const str = String(t ?? '');
  if (!q) return str;
  const i = str.toLowerCase().indexOf(q);
  if (i < 0) return str;
  return <>{str.slice(0, i)}<mark className="bh-hl">{str.slice(i, i + q.length)}</mark>{str.slice(i + q.length)}</>;
};

const ASSET_KINDS = [
  { k: 'order', label: 'แบบฟอร์ม / ไฟล์สั่งซื้อ', hint: 'order form · เทมเพลตใบสั่งซื้อ · แคตตาล็อกสั่งซื้อรอบล่าสุด' },
  { k: 'price', label: 'Price list / เงื่อนไขราคา', hint: 'ราคาส่ง ส่วนลด รอบปรับราคา (ไฟล์เก็บส่วนตัว ไม่เปิดสาธารณะ)' },
  { k: 'brand', label: 'Brand asset — โลโก้ / รูป / สื่อ', hint: 'วางลิงก์ Google Drive / Dropbox ของแบรนด์ได้เลย' },
  { k: 'contract', label: 'สัญญา / ข้อตกลงตัวแทน', hint: '' },
  { k: 'other', label: 'ไฟล์อื่นๆ', hint: '' },
];

const FACTS = [
  ['website', 'เว็บไซต์', 'link'], ['order_email', 'อีเมล / ช่องทางสั่งซื้อ', 'mail'], ['order_channel', 'วิธีสั่งซื้อ'],
  ['payment_terms', 'เงื่อนไขชำระเงิน'], ['default_incoterm', 'Incoterm ประจำ'],
  ['default_currency', 'สกุลเงิน'], ['lead_time_days', 'Lead time', 'days'], ['moq', 'ขั้นต่ำ (MOQ)'],
  ['ship_from', 'ส่งจาก'], ['forwarder', 'Forwarder ประจำ'], ['address', 'ที่อยู่'],
];

// ── เวลาไทยเสมอ (Asia/Bangkok, +07:00 ชัดเจน — ไม่พึ่ง timezone ของเครื่อง: อยู่ญี่ปุ่นก็ใส่/อ่านเป็นเวลาไทย) ──
const pad = (n) => String(n).padStart(2, '0');
const BKK = 'Asia/Bangkok';
const bkkParts = (iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: BKK, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    .formatToParts(new Date(iso)).map(x => [x.type, x.value]));
  if (p.hour === '24') p.hour = '00';   // quirk ของบางเบราว์เซอร์ตอนเที่ยงคืน
  return p;
};
const toLocalInput = (iso) => { if (!iso) return ''; const p = bkkParts(iso); return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`; };
const fromLocalInput = (v) => (v ? new Date(v.length === 16 ? `${v}:00+07:00` : `${v}+07:00`).toISOString() : null);
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: BKK }) : '';
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', timeZone: BKK }) : '';
const dateOnly = (iso) => { const p = bkkParts(iso); return `${p.year}-${p.month}-${p.day}`; };
const timeOnly = (iso) => { const p = bkkParts(iso); return `${p.hour}:${p.minute}`; };
const fmtDue = (r) => {
  if (!r.due_date) return 'ไม่มีกำหนด';
  const [y, m, d] = String(r.due_date).slice(0, 10).split('-').map(Number);
  const s = new Date(y, m - 1, d).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });
  return r.due_time ? `${s} ${r.due_time}` : s;
};
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// ── รอบสั่งซื้อประจำปี: คำนวณวันครบรอบ + วันเตือน (คิดเป็นวันที่ล้วน ไม่ยุ่งกับ timezone) ──
const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const todayBkk = () => { const p = bkkParts(new Date().toISOString()); return `${p.year}-${p.month}-${p.day}`; };
const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const shiftDays = (ymd, delta) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};
// นับวันแบบวันที่ล้วน อิงวันนี้ตามเวลาไทย (ไม่พึ่ง timezone ของเครื่อง)
const daysUntilBkk = (ymd) => {
  if (!ymd) return Infinity;
  const [y1, m1, d1] = String(ymd).slice(0, 10).split('-').map(Number);
  const [y2, m2, d2] = todayBkk().split('-').map(Number);
  return Math.round((Date.UTC(y1, m1 - 1, d1) - Date.UTC(y2, m2 - 1, d2)) / 86400000);
};
// คืน { target: วันครบรอบถัดไป, remind: วันที่จะเตือน }
// ⚠️ ยึด "วันครบรอบ" เป็นตัวตัดสินว่าปีนี้หรือปีหน้า — ถ้ายึดวันเตือน รอบที่เข้าช่วงเตือนแล้ว
//    (เช่น ครบ 1 ก.พ. เตือนล่วงหน้า 30 วัน พอถึง 10 ม.ค.) จะกระโดดไปปีหน้าทั้งที่ยังไม่ถึงกำหนด
const cycleDates = (row) => {
  const today = todayBkk();
  const lead = Math.max(0, Number(row.lead_days ?? 0) || 0);
  const mk = (yy) => `${yy}-${pad(row.month)}-${pad(Math.min(Number(row.day) || 1, daysInMonth(yy, row.month)))}`;
  let y = Number(today.slice(0, 4));
  let target = mk(y);
  if (target < today) target = mk(y + 1);
  return { target, remind: shiftDays(target, -lead), lead };
};
const cycleText = (row) => `${row.day ? `${row.day} ` : 'ภายในเดือน '}${TH_MON[(row.month || 1) - 1]}`;
// 'YYYY-MM-DD' → '1 ต.ค. 69' (คำนวณจากตัวเลขล้วน ไม่แตะ timezone)
const fmtYmd = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return `${d} ${TH_MON[m - 1]} ${String((y + 543) % 100).padStart(2, '0')}`;
};
const cycleTitle = (row, brand) => `รอบสั่งซื้อ: ${row.label} — ${brand}`;

// ── ไฟล์แนบ: วันหมดอายุ (price list / สัญญา) ──
const assetState = (a) => {
  if (!a?.valid_to) return null;
  const d = daysUntilBkk(a.valid_to);
  return { d, level: d < 0 ? 'exp' : d <= 30 ? 'warn' : 'ok' };
};
const assetBadge = (st, a) => st.level === 'exp' ? `หมดอายุแล้ว ${-st.d} วัน`
  : st.level === 'warn' ? `เหลือ ${st.d} วัน` : `ใช้ได้ถึง ${fmtYmd(a.valid_to)}`;
// ปักหมุดก่อน แล้วเรียงเวลาที่เกิดจริง (held_at) ใหม่→เก่า — ใช้ทั้ง mock และ live ให้ลำดับตรงกัน
const sortNotes = (list) => {
  const t = (n) => new Date(n.held_at || n.created_at || 0).getTime();
  return [...list].sort((a, b) => (Number(!!b.pinned) - Number(!!a.pinned)) || (t(b) - t(a)));
};

const Ico = ({ d, sw = 2 }) => (
  <svg viewBox="0 0 24 24" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none" dangerouslySetInnerHTML={{ __html: d }}/>
);
const FileIcon = () => <Ico d='<path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M13 3v5h5"/>'/>;
const LinkIcon = () => <Ico d='<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>'/>;
const Arrow = () => <span className="t-arw">→</span>;

// ── ไฟล์/ลิงก์ 1 ชิ้น (path = bucket ส่วนตัว → signed URL ตอนเปิด · url = ลิงก์ภายนอก) ──
function FileChip({ f, onOpen, onRemove, onEdit }) {
  const st = assetState(f);
  return (
    <div className={`fchip bh-fchip ${st ? 'st-' + st.level : ''}`}>
      <span className="fchip-ic" onClick={() => onOpen(f)}>{f.url ? <LinkIcon/> : <FileIcon/>}</span>
      <span className="fchip-bd">
        <span className="fchip-nm" onClick={() => onOpen(f)} title={f.name}>{f.name || 'ไฟล์แนบ'}</span>
        {(f.version_label || st) && (
          <span className="fchip-meta">
            {f.version_label && <span className="fchip-ver">{f.version_label}</span>}
            {st && <span className={`pl-badge ${st.level}`}>{assetBadge(st, f)}</span>}
          </span>
        )}
      </span>
      <button type="button" className="fchip-open" onClick={() => onOpen(f)}>เปิด</button>
      {onEdit && <button type="button" className="fchip-open" onClick={() => onEdit(f)} title="แก้ชื่อ / เวอร์ชัน / วันหมดอายุ">✎</button>}
      {onRemove && <button type="button" className="fchip-x" onClick={() => onRemove(f)} title="เอาออก">✕</button>}
    </div>
  );
}

// ══════════════ ข้อมูลตัวอย่าง (โหมด MOCK — ดูหน้าตา/ลองกดได้ ไม่แตะ DB) ══════════════
function mockData(now = new Date()) {
  const day = (off) => { const d = new Date(now); d.setDate(d.getDate() + off); return dayKey(d); };
  const at = (off, h = 10) => { const d = new Date(now); d.setDate(d.getDate() + off); d.setHours(h, 0, 0, 0); return d.toISOString(); };
  const partners = [
    { id: 'p1', supplier_name: 'Snow Peak', company_name: 'Snow Peak Inc.', aliases: ['Snow Peak Festival', 'Snow Peak 60th'], country: 'JP',
      website: 'https://www.snowpeak.co.jp', order_email: 'export@snowpeak.co.jp', order_channel: 'ส่ง order form (Excel) ทางอีเมลถึงทีม Export',
      order_cycle: 'Pre-order ปีละ 2 รอบ (SS / AW) + เติมสต๊อกได้ทุกเดือน', payment_terms: 'T/T 100% ก่อนส่งของ', default_incoterm: 'EXW',
      default_currency: 'JPY', lead_time_days: 45, moq: 'ไม่มี (ตามรอบ)', ship_from: 'Sanjo, Niigata', forwarder: 'Yusen',
      brand_profile: 'แบรนด์แคมป์ปิ้งพรีเมียมจากญี่ปุ่น (ก่อตั้ง 1958, Sanjo) — งานโลหะ/ไทเทเนียม เต็นท์ ทาร์ป เฟอร์นิเจอร์แคมป์ ดีไซน์เรียบ ทนทาน ราคาสูง กลุ่มลูกค้า: นักแคมป์สายจริงจัง/สะสม · สินค้าหลักที่เราสั่ง: เต็นท์ IGT ไทเทเนียม เก้าอี้ Snow Peak Festival (สินค้าจำกัดรอบ)',
      key_facts: 'Form JTEPA ต้องขอก่อนของออกจากญี่ปุ่น — ออกย้อนหลังไม่ได้\nราคาส่งปรับทุก 1 ก.พ. · แจ้งยอด pre-order ก่อน 15 ต.ค. / 15 เม.ย.',
      quirks: 'ส่ง CO ช้า ~5 วันหลัง B/L ต้องทวงทุกครั้ง', handler: 'ปุ้ย', brand_hue: '#8b5cf6', is_active: true,
      order_cycles: [
        { id: 'oc1', label: 'Pre-order AW', month: 10, day: 15, lead_days: 14, note: 'ส่งยอดรวมทุกสาขาก่อนปิดรับ' },
        { id: 'oc2', label: 'Pre-order SS', month: 4, day: 15, lead_days: 14, note: null },
        { id: 'oc3', label: 'ราคาส่งปรับใหม่', month: 2, day: 1, lead_days: 30, note: 'เช็กราคาก่อนออก PO' },
      ],
      assets: [
        { id: 'a1', kind: 'order', name: 'SnowPeak_OrderForm_2026.xlsx', path: 'x', version_label: '2026' },
        { id: 'a2', kind: 'brand', name: 'Brand asset (Google Drive)', url: 'https://drive.google.com' },
        { id: 'a3', kind: 'price', name: 'Wholesale price AW26.pdf', path: 'x', version_label: 'AW26', valid_to: day(20) },
        { id: 'a4', kind: 'contract', name: 'Distributor agreement 2024.pdf', path: 'x', version_label: '2024', valid_to: day(-40) },
      ] },
    { id: 'p2', supplier_name: 'DoD', company_name: "Bee's Co., Ltd.", aliases: ['DOD', 'DOD Outdoors'], country: 'JP',
      order_email: 'trade@dod.camp', order_channel: 'B2B portal', order_cycle: 'สั่งได้ตลอด', payment_terms: 'T/T 50% มัดจำ · 50% ก่อนส่ง',
      default_incoterm: 'FOB', default_currency: 'JPY', lead_time_days: 30, handler: 'ปุ้ย', brand_hue: '#f0930c', is_active: true, assets: [] },
    { id: 'p3', supplier_name: 'NALGENE', company_name: 'Nalgene Outdoor', aliases: ['Nalgene'], country: 'US',
      order_email: 'orders@nalgene.com', payment_terms: 'Net 30', default_incoterm: 'EXW', default_currency: 'USD', lead_time_days: 60,
      key_facts: 'ของมาเป็นกล่อง 6 ขวด — บันทึกจำนวนต่อชิ้นเสมอ', brand_hue: '#2f6bff', is_active: true, assets: [] },
  ];
  const contacts = [
    { id: 'k1', partner_id: 'p1', name: 'Kenya Sato', role: 'Export Sales (Asia)', email: 'k.sato@snowpeak.co.jp', chat_id: 'LINE: kenya_sp', languages: 'EN, JP', is_primary: true, note: 'ตอบเร็วช่วงเช้าญี่ปุ่น · เรื่องเคลมส่งคนนี้' },
    { id: 'k2', partner_id: 'p1', name: 'Yuki Tanaka', role: 'Accounting', email: 'ar@snowpeak.co.jp', is_primary: false, note: 'ส่งสลิปโอนให้ทุกครั้ง' },
    { id: 'k3', partner_id: 'p2', name: 'Mai', role: 'Trade support', email: 'mai@dod.camp', is_primary: true },
  ];
  const notes = [
    { id: 'n1', partner_id: 'p1', kind: 'meeting', title: 'ประชุม pre-order AW26', held_at: at(5, 14), agenda: '1) ยอดสั่ง AW26 2) สินค้าใหม่ 60th 3) กำหนดส่ง', summary: '', attendees: 'ปุ้ย, Kenya', files: [], pinned: false },
    { id: 'n2', partner_id: 'p1', kind: 'meeting', title: 'สรุปเคลมถุงคลุมโต๊ะขาด', held_at: at(-9, 11), agenda: 'เคลม lot ก.ค.', summary: 'Kenya ส่ง replacement ให้ Tokyo office · tracking 3549-7125-1014 · ครั้งหน้าถ่ายรูปก่อนเปิดกล่อง', attendees: 'ปุ้ย, Kenya', files: [{ id: 'f1', name: 'claim_photos.zip', path: 'x' }], pinned: true },
    { id: 'n3', partner_id: 'p2', kind: 'email', title: 'ยืนยันราคา 2026', held_at: at(-20, 9), summary: 'ราคาเดิมถึงสิ้นปี · ค่าส่งเพิ่ม 3%', files: [], pinned: false },
    { id: 'n4', partner_id: 'p1', kind: 'call', title: 'โทรคุยเรื่องของเสียหาย lot ส.ค.', held_at: at(-2, 15), agenda: '', summary: '', attendees: 'ปุ้ย, Kenya', files: [], pinned: false },
  ];
  const reminders = [
    { id: 'r1', partner_id: 'p1', note_id: 'n1', title: 'ประชุม / นัดหมาย: ประชุม pre-order AW26 — Snow Peak', due_date: day(5), due_time: '14:00', done: false },
    { id: 'r2', partner_id: 'p1', title: 'ส่งยอด pre-order AW26 ก่อน 15 ต.ค.', due_date: day(12), due_time: null, done: false, note: 'รวมยอดจากทุกสาขา' },
    { id: 'r3', partner_id: 'p2', title: 'ทวงใบเสนอราคา lantern ใหม่', due_date: day(-1), due_time: null, done: true },
    { id: 'r4', partner_id: 'p1', note_id: 'n4', title: 'โทร / วิดีโอคอล: โทรคุยเรื่องของเสียหาย lot ส.ค. — Snow Peak', due_date: day(-2), due_time: '15:00', done: false },
  ];
  const ships = [
    { id: 's1', code: 'SHP-2607-01', label: 'Snow Peak · เติมสต๊อก', mode: 'sea', status: 'docs_received', eta: day(2), etd: day(-20), origin_port: 'Tokyo', dest_port: 'แหลมฉบัง', updated_at: at(-1),
      consignment: [{ id: 'c1', shipment_id: 's1', supplier_name: 'Snow Peak', invoice_no: 'SP-26071', origin_country: 'JP', incoterm: 'EXW', currency: 'JPY' }],
      document_requirement: [
        { id: 'q1', shipment_id: 's1', consignment_id: 'c1', req_type: 'certificate_of_origin', required: true, status: 'missing' },
        { id: 'q2', shipment_id: 's1', consignment_id: null, req_type: 'goods_receipt', required: true, status: 'missing' },
        { id: 'q3', shipment_id: 's1', consignment_id: 'c1', req_type: 'packing_list', required: true, status: 'received' },
      ] },
    { id: 's2', code: 'SHP-2606-03', label: null, mode: 'sea', status: 'received', eta: day(-40), etd: day(-70), origin_port: 'Osaka', dest_port: 'แหลมฉบัง', updated_at: at(-30),
      consignment: [{ id: 'c2', shipment_id: 's2', supplier_name: 'DOD Outdoors', invoice_no: 'DOD-0553', origin_country: 'JP' }], document_requirement: [] },
    { id: 's3', code: 'SHP-2605-02', label: null, mode: 'air', status: 'received', eta: day(-80), etd: day(-85), origin_port: 'Tokyo', dest_port: 'สุวรรณภูมิ', updated_at: at(-60),
      consignment: [{ id: 'c3', shipment_id: 's3', supplier_name: 'Snow Peak', invoice_no: 'SP-26052', origin_country: 'JP' }], document_requirement: [] },
  ];
  const docs = [
    { id: 'd1', shipment_id: 's1', consignment_id: 'c1', req_type: 'proforma_invoice', file_name: 'PI_SP-26071.pdf', file_path: 'x', uploaded_at: at(-25) },
    { id: 'd2', shipment_id: 's1', consignment_id: 'c1', req_type: 'payment_proof', file_name: 'SWIFT_SP-26071.pdf', file_path: 'x', uploaded_at: at(-22) },
    { id: 'd3', shipment_id: 's1', consignment_id: 'c1', req_type: 'commercial_invoice', file_name: 'INV_SP-26071.pdf', file_path: 'x', uploaded_at: at(-10) },
    { id: 'd4', shipment_id: 's1', consignment_id: 'c1', req_type: 'packing_list', file_name: 'PL_SP-26071.pdf', file_path: 'x', uploaded_at: at(-10) },
    { id: 'd5', shipment_id: 's1', consignment_id: null, req_type: 'booking_confirmation', file_name: 'BKG_Yusen.pdf', file_path: 'x', uploaded_at: at(-12) },
    { id: 'd6', shipment_id: 's3', consignment_id: 'c3', req_type: 'commercial_invoice', file_name: 'INV_SP-26052.pdf', file_path: 'x', uploaded_at: at(-80) },
    { id: 'd7', shipment_id: 's3', consignment_id: 'c3', req_type: 'awb', file_name: 'AWB_98765.pdf', file_path: 'x', uploaded_at: at(-82) },
    { id: 'd8', shipment_id: 's2', consignment_id: 'c2', req_type: 'bl', file_name: 'BL_DOD.pdf', file_path: 'x', uploaded_at: at(-45) },
  ];
  const watchouts = [{ supplier_name: 'Snow Peak', kind: 'lesson', text: 'ถ่ายรูปสภาพกล่องก่อนเปิดทุกลัง — ใช้เคลมได้', from_shipment_code: 'SHP-2606-01', created_at: at(-30) }];
  const brandCounts = { 'snow peak': 1817, 'dod': 669, 'nalgene': 58 };
  return { partners, contacts, notes, reminders, ships, docs, watchouts, brandCounts };
}

// ══════════════ Modals ══════════════
function AliasEditor({ value, onChange, suggestions, inputId }) {
  const [q, setQ] = useState('');
  const add = (s) => { const v = (s || '').trim(); if (!v || value.some(a => db.normName(a) === db.normName(v))) { setQ(''); return; } onChange([...value, v]); setQ(''); };
  const sug = suggestions.filter(s => !value.some(a => db.normName(a) === db.normName(s)));
  return (
    <div className="bh-alias">
      <div className="matpick-box" onClick={e => e.currentTarget.querySelector('input')?.focus()}>
        {value.map(a => (
          <span className="matchip" key={a}>{a}<button type="button" onClick={() => onChange(value.filter(x => x !== a))} title="เอาออก">✕</button></span>
        ))}
        <input id={inputId} value={q} onChange={e => setQ(e.target.value)} placeholder={value.length ? 'พิมพ์เพิ่ม แล้ว Enter' : 'เช่น DOD, DOD Outdoors — พิมพ์แล้ว Enter'}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(q); } }} onBlur={() => q.trim() && add(q)}/>
      </div>
      {sug.length > 0 && (
        <div className="bh-alias-sug">
          <span>ชื่อที่พบในตู้/คลังสินค้าแต่ยังไม่ผูกกับแบรนด์ไหน — กดเพื่อผูก:</span>
          {sug.slice(0, 12).map(s => <button type="button" key={s} className="bh-sug" onClick={() => add(s)}>＋ {s}</button>)}
        </div>
      )}
    </div>
  );
}

function PartnerModal({ item, suggestions, onClose, onSave, onDelete, shipCount, isAdmin }) {
  const [f, setF] = useState({
    supplier_name: item?.supplier_name ?? '', company_name: item?.company_name ?? '', aliases: item?.aliases ?? [],
    brand_profile: item?.brand_profile ?? '', is_active: item ? item.is_active !== false : true,
    country: item?.country ?? '', website: item?.website ?? '', address: item?.address ?? '',
    order_email: item?.order_email ?? '', order_channel: item?.order_channel ?? '', order_cycle: item?.order_cycle ?? '',
    payment_terms: item?.payment_terms ?? '', moq: item?.moq ?? '', lead_time_days: item?.lead_time_days ?? '',
    default_incoterm: item?.default_incoterm ?? '', default_currency: item?.default_currency ?? '',
    ship_from: item?.ship_from ?? '', forwarder: item?.forwarder ?? '', key_facts: item?.key_facts ?? '',
    quirks: item?.quirks ?? '', handler: item?.handler ?? '', brand_hue: item?.brand_hue ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = {};
      for (const [k, v] of Object.entries(f)) out[k] = (typeof v === 'string' ? v.trim() : v) || null;
      out.aliases = f.aliases;
      out.is_active = !!f.is_active;
      out.lead_time_days = f.lead_time_days === '' || f.lead_time_days == null ? null : Number(f.lead_time_days);
      if (!out.supplier_name) throw new Error('ใส่ชื่อแบรนด์/บริษัทที่ใช้เรียกก่อน');
      await onSave(out); onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };

  return (
    <Modal wide title={item ? `แก้ข้อมูล — ${item.supplier_name}` : 'เพิ่มแบรนด์ / บริษัท'}
      sub="ใส่เท่าที่รู้ก่อน กลับมาเติมทีหลังได้" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="bh-msec">① ชื่อ & ตัวตน</div>
        <div className="frow">
          <Fld label="ชื่อแบรนด์ / ชื่อที่ทีมใช้เรียก *" hint="ชื่อสั้นๆ ที่เห็นแล้วรู้ทันที เช่น Snow Peak"><input value={f.supplier_name} onChange={set('supplier_name')} required autoFocus/></Fld>
          <Fld label="ชื่อบริษัท (ถ้าต่างจากแบรนด์)" hint="เช่น Bee's Co., Ltd. ของ DoD"><input value={f.company_name} onChange={set('company_name')}/></Fld>
          <Fld label="ประเทศ"><CountrySelect value={f.country} onChange={v => setF({ ...f, country: v })}/></Fld>
        </div>
        <Fld as="div" htmlFor="f-alias" label="ชื่อสะกดอื่นที่ใช้ในตู้ / คลังสินค้า" hint="ระบบใช้ชื่อพวกนี้ดึงตู้ เอกสาร สินค้า และบทเรียนของแบรนด์นี้มาให้อัตโนมัติ — ไม่สนตัวพิมพ์เล็ก/ใหญ่">
          <AliasEditor inputId="f-alias" value={f.aliases} onChange={v => setF({ ...f, aliases: v })} suggestions={suggestions}/>
        </Fld>
        <Fld label="ภาพรวม / character ของแบรนด์" hint="แบรนด์นี้คืออะไร ขายอะไร จุดเด่น สไตล์ กลุ่มลูกค้า สินค้าหลักที่เราสั่ง — อ่านแล้วรู้จักแบรนด์ทันที">
          <textarea className="bh-ta" rows={4} value={f.brand_profile} onChange={set('brand_profile')}
            placeholder={'เช่น แบรนด์แคมป์ปิ้งพรีเมียมจากญี่ปุ่น งานโลหะ/ไทเทเนียม ดีไซน์เรียบ ราคาสูง\nกลุ่มลูกค้า: นักแคมป์สายจริงจัง · สินค้าหลักที่เราสั่ง: …'}/>
        </Fld>
        <div className="frow">
          <Fld label="เว็บไซต์"><input value={f.website} onChange={set('website')} placeholder="https://"/></Fld>
          <Fld label="ที่อยู่ (สั้นๆ)"><input value={f.address} onChange={set('address')}/></Fld>
        </div>

        <div className="bh-msec">② สั่งซื้ออย่างไร</div>
        <div className="frow">
          <Fld label="อีเมล / ช่องทางสั่งซื้อหลัก" hint="อีเมลกลางของบริษัท (คนติดต่อรายคนใส่ที่ “ผู้ติดต่อ”)"><input value={f.order_email} onChange={set('order_email')}/></Fld>
          <Fld label="วิธีสั่ง" hint="เช่น ส่ง order form ทางอีเมล · B2B portal · ผ่านตัวแทน"><input value={f.order_channel} onChange={set('order_channel')}/></Fld>
          <Fld label="ขั้นต่ำ (MOQ)"><input value={f.moq} onChange={set('moq')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="เงื่อนไขชำระเงิน" hint="เช่น T/T 100% ก่อนส่ง · มัดจำ 30%"><input value={f.payment_terms} onChange={set('payment_terms')}/></Fld>
          <Fld label="Lead time (วัน)" hint="สั่งแล้วกี่วันของพร้อมส่ง"><input type="number" min="0" value={f.lead_time_days} onChange={set('lead_time_days')}/></Fld>
          <Fld label="รอบสั่งซื้อ" hint="ใส่วันจริงได้ที่กล่อง “รอบสั่งซื้อ & วันสำคัญ” ในหน้าแบรนด์ แล้วกดตั้งเตือนได้เลย">
            <input value={f.order_cycle} onChange={set('order_cycle')} placeholder="อธิบายสั้นๆ (ถ้ามี)"/>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="Incoterm ประจำ">
            <select value={f.default_incoterm} onChange={set('default_incoterm')}>
              <option value="">— ไม่ระบุ —</option>
              {['EXW', 'FOB', 'FCA', 'CIF', 'CFR', 'DAP', 'DDP'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Fld>
          <Fld label="สกุลเงิน">
            <select value={f.default_currency} onChange={set('default_currency')}>
              <option value="">— ไม่ระบุ —</option>
              {['JPY', 'USD', 'EUR', 'GBP', 'CNY', 'KRW', 'THB', 'AUD'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Fld>
          <Fld label="ส่งจาก (เมือง/ท่า)"><input value={f.ship_from} onChange={set('ship_from')}/></Fld>
          <Fld label="Forwarder ประจำ"><input value={f.forwarder} onChange={set('forwarder')} placeholder="เช่น Yusen"/></Fld>
        </div>

        <div className="bh-msec">③ สิ่งที่ต้องรู้ / ระวัง</div>
        <Fld label="ข้อมูลสำคัญ" hint="กติกา/ข้อตกลง/วันสำคัญ ที่ถ้าไม่รู้แล้วจะพลาด — เขียนเป็นข้อๆ ได้">
          <textarea className="bh-ta" rows={4} value={f.key_facts} onChange={set('key_facts')} placeholder={'เช่น Form JTEPA ต้องขอก่อนของออก ออกย้อนหลังไม่ได้\nราคาส่งปรับทุก 1 ก.พ.'}/>
        </Fld>
        <Fld label="ระวังเป็นพิเศษ / นิสัยของเจ้านี้" hint="เช่น ส่ง CO ช้าเสมอ ต้องทวง · ตอบอีเมลช้าช่วงปิดปีใหม่">
          <textarea className="bh-ta" rows={2} value={f.quirks} onChange={set('quirks')}/>
        </Fld>
        <div className="frow">
          <Fld label="ผู้ดูแลฝั่งเรา" hint="ใครเป็นคนหลักที่คุยกับเจ้านี้"><input value={f.handler} onChange={set('handler')}/></Fld>
          <Fld as="div" label="สีประจำแบรนด์ (ใช้ในหน้านี้และ Console)">
            <div className="bh-hues">
              <button type="button" className={`bh-hue ${!f.brand_hue ? 'on' : ''}`} onClick={() => setF({ ...f, brand_hue: '' })} title="อัตโนมัติ">อัตโนมัติ</button>
              {HUES.map(h => <button type="button" key={h} className={`bh-hue ${f.brand_hue === h ? 'on' : ''}`} style={{ background: h }} onClick={() => setF({ ...f, brand_hue: h })} aria-label={h}/>)}
            </div>
          </Fld>
        </div>

        {item && (
          <label className="fld chk">
            <input type="checkbox" checked={!f.is_active} onChange={e => setF({ ...f, is_active: !e.target.checked })}/>
            <span>เลิกใช้แล้ว — ไม่ได้ติดต่อเจ้านี้แล้ว (ย้ายไปท้ายรายการ ข้อมูล/ประวัติยังอยู่ครบ)</span>
          </label>
        )}
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          <div className="mfoot-l">
            {item && onDelete && isAdmin && (confirmDel ? (
              <div className="del-confirm">
                <span>ลบแบรนด์ + ผู้ติดต่อ + บันทึกทั้งหมด? (ตู้/เอกสารไม่กระทบ{shipCount ? ` — มี ${shipCount} ตู้ผูกอยู่` : ''})</span>
                <button type="button" className="btn-del" disabled={busy} onClick={async () => { setBusy(true); try { await onDelete(item); onClose(); } catch (er) { setError(er.message ?? String(er)); setBusy(false); } }}>ยืนยันลบ</button>
                <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
              </div>
            ) : <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบแบรนด์…</button>)}
          </div>
          <div className="mfoot-r">
            <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
            <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function ContactModal({ item, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    name: item?.name ?? '', role: item?.role ?? '', email: item?.email ?? '', phone: item?.phone ?? '',
    chat_id: item?.chat_id ?? '', languages: item?.languages ?? '', is_primary: !!item?.is_primary, note: item?.note ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = {};
      for (const [k, v] of Object.entries(f)) out[k] = typeof v === 'string' ? (v.trim() || null) : v;
      if (!out.name) throw new Error('ใส่ชื่อผู้ติดต่อก่อน');
      await onSave(out); onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  return (
    <Modal title={item ? `แก้ผู้ติดต่อ — ${item.name}` : 'เพิ่มผู้ติดต่อ'} sub="ใครทำอะไรที่แบรนด์นี้ — ติดต่อเรื่องไหนหาใคร" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="ชื่อ *"><input value={f.name} onChange={set('name')} required autoFocus/></Fld>
          <Fld label="ตำแหน่ง / ดูแลเรื่องอะไร" hint="เช่น Export Sales · Accounting · Logistics"><input value={f.role} onChange={set('role')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="อีเมล"><input type="email" value={f.email} onChange={set('email')}/></Fld>
          <Fld label="โทร"><input value={f.phone} onChange={set('phone')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="LINE / WeChat / WhatsApp"><input value={f.chat_id} onChange={set('chat_id')}/></Fld>
          <Fld label="คุยภาษาอะไรได้" hint="เช่น EN, JP"><input value={f.languages} onChange={set('languages')}/></Fld>
        </div>
        <Fld label="โน้ต" hint="เช่น ตอบเร็วช่วงเช้าญี่ปุ่น · เรื่องเคลมส่งคนนี้"><input value={f.note} onChange={set('note')}/></Fld>
        <label className="fld chk">
          <input type="checkbox" checked={f.is_primary} onChange={set('is_primary')}/>
          <span>ผู้ติดต่อหลัก — ติดต่อคนนี้ก่อนเสมอ</span>
        </label>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          <div className="mfoot-l">
            {item && (confirmDel ? (
              <div className="del-confirm">
                <span>ลบผู้ติดต่อนี้?</span>
                <button type="button" className="btn-del" onClick={async () => { await onDelete(item); onClose(); }}>ยืนยันลบ</button>
                <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
              </div>
            ) : <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบ…</button>)}
          </div>
          <div className="mfoot-r">
            <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
            <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function NoteModal({ item, initialKind, hasReminder, canUpload, onUpload, onOpenFile, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    kind: item?.kind ?? initialKind ?? 'meeting', title: item?.title ?? '', held_at: toLocalInput(item?.held_at),
    agenda: item?.agenda ?? '', summary: item?.summary ?? '', attendees: item?.attendees ?? '',
    files: item?.files ?? [], pinned: !!item?.pinned,
  });
  const [remind, setRemind] = useState(item ? !!hasReminder : true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const timed = f.kind === 'meeting' || f.kind === 'call';

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true); setError(null);
    try { const item = await onUpload(file); setF(x => ({ ...x, files: [...x.files, item] })); }
    catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = {
        kind: f.kind, title: f.title.trim(), held_at: fromLocalInput(f.held_at),
        agenda: f.agenda.trim() || null, summary: f.summary.trim() || null, attendees: f.attendees.trim() || null,
        files: f.files, pinned: f.pinned,
      };
      if (!out.title) throw new Error('ใส่หัวข้อก่อน');
      await onSave(out, { remind: remind && !!out.held_at }); onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  const k = kindOf(f.kind);
  return (
    <Modal wide title={item ? `แก้ — ${item.title}` : 'บันทึกการประชุม / โน้ต'} sub="จดให้คนที่ไม่ได้อยู่ในห้องอ่านแล้วเข้าใจ — หัวข้อ · ตกลงอะไร · ใครทำอะไรต่อ" onClose={onClose}>
      <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={onFile} accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt"/>
      <form onSubmit={submit}>
        <div className="jr-kinds">
          {NOTE_KINDS.map(x => (
            <button type="button" key={x.id} className={`jr-kind ${f.kind === x.id ? 'on' : ''}`}
              style={f.kind === x.id ? { borderColor: x.c, color: x.c } : {}} onClick={() => setF({ ...f, kind: x.id })} title={x.hint}>{x.t}</button>
          ))}
        </div>
        <div className="frow">
          <Fld label="หัวข้อ *" hint={k.hint}><input value={f.title} onChange={set('title')} required autoFocus placeholder={f.kind === 'meeting' ? 'เช่น ประชุม pre-order AW26' : 'เช่น ยืนยันราคา 2026'}/></Fld>
          <Fld label={timed ? 'วัน-เวลานัด' : 'วัน-เวลา (ถ้ามี)'} hint="เวลาไทย">
            <input type="datetime-local" value={f.held_at} onChange={set('held_at')}/>
          </Fld>
        </div>
        {timed && (
          <Fld label="วาระ / สิ่งที่จะคุย" hint="ใส่ก่อนประชุม — จะได้รู้ว่าประชุมนี้เรื่องอะไร">
            <textarea className="bh-ta" rows={3} value={f.agenda} onChange={set('agenda')} placeholder={'1) …\n2) …'}/>
          </Fld>
        )}
        <Fld label={timed ? 'สรุปผล / ข้อตกลง / สิ่งที่ต้องทำต่อ' : 'รายละเอียด'} hint="ใส่หลังคุยจบ — สิ่งที่ตกลง ใครทำอะไร ภายในเมื่อไร">
          <textarea className="bh-ta" rows={5} value={f.summary} onChange={set('summary')}/>
        </Fld>
        <div className="frow">
          <Fld label="ผู้เข้าร่วม (เรา / เขา)"><input value={f.attendees} onChange={set('attendees')} placeholder="เช่น ปุ้ย, Kenya (Snow Peak)"/></Fld>
        </div>
        <div className="pa-cat">
          <div className="pa-cat-h">
            <span className="pa-cat-l">ไฟล์แนบของบันทึกนี้ (สไลด์ / ใบเสนอราคา / รูป)</span>
            <button type="button" className="pa-add" disabled={busy || !canUpload} onClick={() => fileRef.current?.click()} title={canUpload ? '' : 'บันทึกได้หลังต่อ Supabase จริง'}>＋ แนบไฟล์</button>
          </div>
          <div className="pa-files">
            {f.files.map(x => <FileChip key={x.id} f={x} onOpen={onOpenFile} onRemove={() => setF({ ...f, files: f.files.filter(y => y.id !== x.id) })}/>)}
            {!f.files.length && <span className="pa-empty">ยังไม่มีไฟล์แนบ</span>}
          </div>
        </div>
        <div className="frow" style={{ marginTop: 10 }}>
          <label className="fld chk">
            <input type="checkbox" checked={remind} onChange={e => setRemind(e.target.checked)} disabled={!f.held_at}/>
            <span>เตือนในแท็บ “วันนี้” + ปฏิทินของแอพ ตามวัน-เวลานี้{!f.held_at ? ' (ใส่วัน-เวลาก่อน)' : ''}</span>
          </label>
          <label className="fld chk">
            <input type="checkbox" checked={f.pinned} onChange={set('pinned')}/>
            <span>ปักไว้บนสุด — ข้อตกลงสำคัญที่ต้องเห็นตลอด</span>
          </label>
        </div>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          <div className="mfoot-l">
            {item && (confirmDel ? (
              <div className="del-confirm">
                <span>ลบบันทึกนี้ (และการเตือนที่ผูกอยู่)?</span>
                <button type="button" className="btn-del" onClick={async () => { await onDelete(item); onClose(); }}>ยืนยันลบ</button>
                <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
              </div>
            ) : <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบ…</button>)}
          </div>
          <div className="mfoot-r">
            <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
            <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ไฟล์/ลิงก์ของแบรนด์ — ใช้ทั้งตอนเพิ่มลิงก์ใหม่ และแก้ของเดิม (ชื่อ / เวอร์ชัน / วันหมดอายุ)
function AssetModal({ item, kind, onClose, onSave }) {
  const isNew = !item;
  const isLink = isNew || !!item.url;
  const [f, setF] = useState({
    name: item?.name ?? '', url: item?.url ?? '',
    version_label: item?.version_label ?? '', valid_to: item?.valid_to ?? '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const u = f.url.trim();
    if (isLink && !/^https?:\/\//i.test(u)) { setError('ลิงก์ต้องขึ้นต้นด้วย http:// หรือ https://'); return; }
    const base = item ?? { id: 'l' + Date.now(), kind };
    setBusy(true); setError(null);
    try {
      await onSave({
        ...base,
        ...(isLink ? { url: u } : {}),
        name: f.name.trim() || (isLink ? u.replace(/^https?:\/\//, '').slice(0, 60) : (item?.name || 'ไฟล์แนบ')),
        version_label: f.version_label.trim() || null,
        valid_to: f.valid_to || null,
      });
      onClose();
    } catch (er) { setError(er.message ?? String(er)); setBusy(false); }
  };
  return (
    <Modal title={isNew ? 'เพิ่มลิงก์' : `แก้ — ${item.name || 'ไฟล์แนบ'}`}
      sub={isNew ? 'เช่น โฟลเดอร์ Google Drive ของแบรนด์ / หน้า B2B portal' : 'ตั้งเวอร์ชันและวันหมดอายุได้ — ใกล้หมดจะขึ้นเตือนที่ “ทำต่อจากตรงนี้”'} onClose={onClose}>
      <form onSubmit={submit}>
        {isLink && <Fld label="ลิงก์ *"><input value={f.url} onChange={set('url')} placeholder="https://" required autoFocus={isNew}/></Fld>}
        <Fld label="ชื่อที่จะแสดง"><input value={f.name} onChange={set('name')} placeholder="เช่น Price list AW26"/></Fld>
        <div className="frow">
          <Fld label="เวอร์ชัน / รุ่น (ถ้ามี)" hint="เช่น AW26 · 2026-v2 — จะได้รู้ว่าอันไหนใหม่กว่า"><input value={f.version_label} onChange={set('version_label')}/></Fld>
          <Fld label="ใช้ได้ถึงวันที่ (ถ้ามี)" hint="ราคา/สัญญาที่มีวันหมด — เหลือ 30 วันระบบจะเตือนให้"><input type="date" value={f.valid_to} onChange={set('valid_to')}/></Fld>
        </div>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot"><button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : isNew ? 'เพิ่มลิงก์' : 'บันทึก'}</button></div>
      </form>
    </Modal>
  );
}

// รอบสั่งซื้อ / วันสำคัญประจำปี — 1 แถว = 1 รอบ (ตั้งเตือนล่วงหน้าได้)
function CycleForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    label: initial?.label ?? '', month: initial?.month ?? '', day: initial?.day ?? '',
    lead_days: initial?.lead_days ?? 7, note: initial?.note ?? '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (!f.label.trim() || !f.month) return;
    onSave({
      id: initial?.id ?? 'oc' + Date.now(), label: f.label.trim(), month: Number(f.month),
      day: f.day === '' ? null : Math.max(1, Math.min(31, Number(f.day))),
      lead_days: f.lead_days === '' ? 0 : Math.max(0, Number(f.lead_days)),
      note: f.note.trim() || null,
    });
  };
  return (
    <form className="t-remform" onSubmit={submit}>
      <input className="t-in" autoFocus placeholder="รอบนี้เรียกว่าอะไร เช่น Pre-order AW · ปิดรับยอด · ราคาปรับใหม่"
        value={f.label} onChange={set('label')}/>
      <div className="t-remrow">
        <label className="t-remfield"><span>เดือน</span>
          <select className="t-in sm" value={f.month} onChange={set('month')}>
            <option value="">— เลือก —</option>
            {TH_MON.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="t-remfield"><span>วันที่ (เว้นได้)</span>
          <input className="t-in sm" type="number" min="1" max="31" value={f.day} onChange={set('day')} placeholder="15"/>
        </label>
        <label className="t-remfield"><span>เตือนล่วงหน้า (วัน)</span>
          <input className="t-in sm" type="number" min="0" max="180" value={f.lead_days} onChange={set('lead_days')}/>
        </label>
      </div>
      <input className="t-in" placeholder="โน้ต เช่น ส่งยอดรวมทุกสาขาก่อนปิดรับ" value={f.note} onChange={set('note')}/>
      <div className="t-rembtns">
        <button className="t-savebtn" disabled={!f.label.trim() || !f.month}>{initial ? 'บันทึกการแก้ไข' : 'เพิ่มรอบ'}</button>
        <button type="button" className="t-cancelbtn" onClick={onCancel}>ยกเลิก</button>
      </div>
    </form>
  );
}

// ══════════════ หน้าแบรนด์ ══════════════
export default function Brands({ onOpenShipment, focusBrand, onFocusConsumed, onSelect }) {
  const { mock, session, me } = useAuth();
  const meId = mock ? 'u1' : (session?.user?.id ?? null);
  const isAdmin = mock || !!me?.is_admin;           // ลบแบรนด์ = admin เท่านั้น (RLS deploy-26) — คนอื่นใช้ "เลิกใช้แล้ว"
  const canCost = mock || !!me?.can_see_cost;      // ราคา/ใบสั่งซื้อ = admin|coordinator|accountant (RLS can_see_cost) — คลังไม่เห็น
  const [staff, setStaff] = useState([]);           // ทีมของแอพ (มอบหมายงาน)
  const [partners, setPartners] = useState([]);
  const [ships, setShips] = useState([]);           // แถว DB + consignment[] (จาก listShipments)
  const [brandNames, setBrandNames] = useState([]); // ชื่อแบรนด์สินค้าในคลัง
  const [productCounts, setProductCounts] = useState({});
  const [sel, setSel] = useState(null);
  const [search, setSearch] = useState('');
  const [st, setSt] = useState({ loading: true });
  const [actMsg, setActMsg] = useState(null);
  const [flash, setFlash] = useState(null);
  const [nonce, setNonce] = useState(0);
  // ข้อมูลของแบรนด์ที่เลือก
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [watchouts, setWatchouts] = useState([]);
  const [detailBusy, setDetailBusy] = useState(false);
  const [modal, setModal] = useState(null);         // {t:'partner',item} | {t:'contact',item} | {t:'note',item,kind} | {t:'link',kind}
  const [noteFilter, setNoteFilter] = useState('all');
  const [docFilter, setDocFilter] = useState('all');
  const [docQ, setDocQ] = useState('');             // ค้นหาเอกสาร (ฝั่ง client ไม่ยิง query)
  const [openShip, setOpenShip] = useState({});     // ตู้ไหนกางอยู่ (ผู้ใช้กดเอง ทับค่าตั้งต้น)
  const [fileList, setFileList] = useState({});     // ตู้ไหนเปิดรายชื่อไฟล์อยู่
  const [kfOpen, setKfOpen] = useState(false);   // ย่อ "สิ่งที่ต้องรู้" ถ้ายาวมาก
  const [remOpen, setRemOpen] = useState(false);
  const [remF, setRemF] = useState({ title: '', date: '', time: '', note: '', assignee: '' });
  const staffName = (id) => staff.find(s => s.user_id === id)?.name || null;
  const [zipBusy, setZipBusy] = useState(false);
  const [cycOpen, setCycOpen] = useState(false);     // ฟอร์มเพิ่มรอบสั่งซื้อ
  const [cycEdit, setCycEdit] = useState(null);      // id รอบที่กำลังแก้
  const [cycDel, setCycDel] = useState(null);        // id รอบที่กำลังยืนยันลบ
  const [signer, reloadSigner] = useSigner();        // ผู้จัดทำ (พิมพ์สมุดส่งมอบ)
  const mockRef = useRef(null);
  const assetFile = useRef(null);
  const assetKind = useRef('other');
  const canWrite = !mock;

  const say = (m) => { setFlash(m); setTimeout(() => setFlash(null), 3500); };

  // ── โหลดรายชื่อแบรนด์ + ตู้ทั้งหมด (ครั้งเดียว) ──
  useEffect(() => {
    if (mock) {
      const m = mockRef.current ?? (mockRef.current = mockData());
      setPartners(m.partners); setShips(m.ships); setBrandNames(['Snow Peak', 'DoD', 'NALGENE', 'TOKYO CRAFTS', 'Snow Peak Festival']);
      setProductCounts(m.brandCounts); setStaff([{ user_id: 'u1', name: 'ปุ้ย' }, { user_id: 'u2', name: 'ปรินทร' }]);
      setSt({ loading: false, demo: true });
      return;
    }
    let alive = true;
    setSt({ loading: true });
    (async () => {
      try {
        const [parts, shipRows, brands, counts, team] = await Promise.all([
          db.listPartners(), db.listShipments(), db.classificationBrands().catch(() => []), db.partnerProductCounts(),
          db.listStaff().catch(() => []),
        ]);
        if (!alive) return;
        setPartners(parts); setShips(shipRows); setStaff(team.filter(s => s.is_active !== false));
        setBrandNames(brands.map(b => (b.brand || '').trim()).filter(Boolean));
        setProductCounts(counts);
        setSt({ loading: false });
      } catch (e) {
        if (!alive) return;
        if (isSchemaPending(e)) setSt({ loading: false, pending: true });
        else setSt({ loading: false, error: e.message ?? String(e) });
      }
    })();
    return () => { alive = false; };
  }, [mock, nonce]);

  // เปิดแบรนด์ที่ถูกส่งมาจากแท็บ "วันนี้" / จาก URL ตอนรีเฟรช
  useEffect(() => {
    if (!focusBrand || st.loading) return;
    if (st.error || st.pending) return;   // โหลดไม่ขึ้น → เก็บแบรนด์ที่สั่งเปิดไว้ก่อน กด "ลองใหม่" แล้วค่อยเปิด (ไม่ทิ้งลิงก์)
    const found = partners.some(p => p.id === focusBrand);
    if (found) setSel(focusBrand);
    onFocusConsumed?.(found);   // false = แบรนด์นี้ไม่มีแล้ว → App บอกคน
  }, [focusBrand, st.loading, partners]);

  // แบรนด์ที่เปิดอยู่ → ใส่ใน URL (รีเฟรชแล้วกลับมาแบรนด์เดิม) · รอแบรนด์ที่สั่งเปิดถูกเลือกก่อน
  useEffect(() => {
    if (focusBrand || st.loading || st.error || st.pending) return;
    onSelect?.(sel);
  }, [sel, focusBrand, st.loading, st.error, st.pending]);

  const cur = partners.find(p => p.id === sel) ?? null;
  const selNow = useRef(sel);       // แบรนด์ที่เปิดอยู่ "ตอนนี้" — loadDetail ที่ตอบช้าเช็คกับค่านี้
  selNow.current = sel;
  const detailReq = useRef({});     // { partnerId: เลขคำขอล่าสุด }
  const vms = useMemo(() => ships.map(toRailVM), [ships]);
  const shipsOf = useMemo(() => {
    const m = {};
    for (const p of partners) m[p.id] = db.partnerShipmentsOf(p, ships);
    return m;
  }, [partners, ships]);

  // ── โหลดรายละเอียดของแบรนด์ที่เลือก ──
  const loadDetail = async (p) => {
    if (!p) return;
    if (mock) {
      const m = mockRef.current;
      setContacts(m.contacts.filter(c => c.partner_id === p.id));
      setNotes(sortNotes(m.notes.filter(n => n.partner_id === p.id)));
      setReminders(m.reminders.filter(r => r.partner_id === p.id));
      const sids = new Set(shipsOf[p.id].map(s => s.id));
      setDocs(m.docs.filter(d => sids.has(d.shipment_id)));
      setWatchouts(m.watchouts.filter(w => db.partnerMatches(p, w.supplier_name)));
      return;
    }
    // คำตอบที่มาช้าห้ามเขียนทับแบรนด์ที่เปิดอยู่ (รีวิว 28 ต.ค. 69 — เดิมกดแบรนด์ใหญ่แล้วกดแบรนด์เล็กต่อ
    //   ผู้ติดต่อ/บันทึก/เอกสารของแบรนด์ใหญ่ขึ้นใต้หัวแบรนด์เล็ก) · เช็คทั้ง "ยังเปิดแบรนด์นี้อยู่" และ "เป็นคำขอล่าสุดของแบรนด์นี้"
    //   (ปุ่มบันทึก/ลบ เรียก loadDetail(cur) หลัง await → ถ้าคนกดไปแบรนด์อื่นแล้ว ต้องไม่ทับของแบรนด์นั้น)
    const my = (detailReq.current[p.id] = (detailReq.current[p.id] ?? 0) + 1);
    const fresh = () => detailReq.current[p.id] === my && selNow.current === p.id;
    if (selNow.current === p.id) setDetailBusy(true);
    try {
      const myShips = shipsOf[p.id] ?? [];
      const consIds = myShips.flatMap(s => (s.consignment ?? []).filter(c => db.partnerMatches(p, c.supplier_name)).map(c => c.id));
      const [c, n, r, d, w] = await Promise.all([
        db.listPartnerContacts(p.id), db.listPartnerNotes(p.id), db.listPartnerReminders(p.id),
        db.partnerDocuments(myShips.map(s => s.id), consIds), db.getWatchouts(db.partnerRawNames(p, myShips), null).catch(() => []),
      ]);
      if (fresh()) { setContacts(c); setNotes(sortNotes(n)); setReminders(r); setDocs(d); setWatchouts(w); }
    } catch (e) { if (fresh()) setActMsg(e.message ?? String(e)); }
    if (fresh()) setDetailBusy(false);
  };
  // เปลี่ยนแบรนด์ = ล้างฟอร์ม/ตัวกรองของแบรนด์เก่า (แยกจาก effect โหลดข้อมูล ไม่งั้นบันทึกทีเดียวตัวกรองรีเซ็ต)
  //   + ล้างรายการของแบรนด์เก่า ไม่ให้ค้างใต้หัวแบรนด์ใหม่ระหว่างรอโหลด
  useEffect(() => {
    setKfOpen(false); setNoteFilter('all'); setDocFilter('all'); setRemOpen(false); setCycOpen(false); setCycEdit(null); setDocQ(''); setOpenShip({}); setFileList({});
    setContacts([]); setNotes([]); setReminders([]); setDocs([]); setWatchouts([]);
  }, [cur?.id]);
  const myShipIds = (shipsOf[cur?.id] ?? []).map(s => s.id).join(',');
  useEffect(() => { loadDetail(cur); }, [cur?.id, myShipIds]);

  const refreshPartners = async () => {
    if (mock) { setPartners([...mockRef.current.partners]); return; }
    setPartners(await db.listPartners());
  };

  // ── ชื่อที่พบในตู้/คลัง แต่ยังไม่ผูกกับแบรนด์ไหน (แนะนำผูก / สร้างอัตโนมัติ) ──
  const unmatched = useMemo(() => {
    const names = new Set(ships.flatMap(s => (s.consignment ?? []).map(c => (c.supplier_name || '').trim()).filter(Boolean)));
    for (const b of brandNames) names.add(b);
    const out = [];
    for (const n of names) {
      if (partners.some(p => db.partnerMatches(p, n))) continue;
      const nn = db.normName(n);
      const parent = partners.find(p => db.partnerNames(p).some(a => nn.startsWith(db.normName(a) + ' ')));
      if (parent) continue;   // เป็นชื่อรุ่นของแบรนด์ที่มี (seed จะผูกให้)
      out.push(n);
    }
    return out.sort();
  }, [ships, brandNames, partners]);
  const unmatchedAliases = useMemo(() => {   // ชื่อที่ seed จะผูกเป็นชื่อสะกดอื่นให้
    const names = new Set(ships.flatMap(s => (s.consignment ?? []).map(c => (c.supplier_name || '').trim()).filter(Boolean)));
    for (const b of brandNames) names.add(b);
    return [...names].filter(n => !partners.some(p => db.partnerMatches(p, n)) && !unmatched.includes(n));
  }, [ships, brandNames, partners, unmatched]);

  const seed = async () => {
    if (mock) { say('โหมดตัวอย่าง — ของจริงจะสร้างแบรนด์จาก supplier ในตู้ + แบรนด์ในคลังสินค้าให้อัตโนมัติ'); return; }
    setDetailBusy(true);
    try {
      const r = await db.seedPartnersFromData(partners);
      await refreshPartners();
      say(`สร้างแบรนด์ใหม่ ${r.created} · ผูกชื่อสะกดเพิ่ม ${r.aliased} — เปิดแต่ละแบรนด์แล้วเติมข้อมูลติดต่อได้เลย`);
    } catch (e) { setActMsg(e.message ?? String(e)); }
    setDetailBusy(false);
  };

  // ── เปิดไฟล์ (path = signed URL · url = ลิงก์) ──
  const openFile = (f) => {
    if (f.url) { window.open(f.url, '_blank', 'noopener'); return; }
    if (!f.path && !f.file_path) return;
    if (mock) { setActMsg('โหมดตัวอย่าง — ไฟล์จำลองเปิดไม่ได้ (ของจริงเปิดผ่านลิงก์ปลอดภัยชั่วคราว 1 ชม.)'); return; }
    const w = window.open('', '_blank');
    db.documentUrl(f.path || f.file_path)
      .then(url => { if (w) w.location.href = url; else window.open(url, '_blank'); })
      .catch(er => { if (w) w.close(); setActMsg(`เปิดไฟล์ไม่สำเร็จ: ${er.message ?? String(er)}`); });
  };
  const uploadFor = async (file) => {
    if (mock) return { id: 'f' + Date.now(), name: file.name, mime: file.type, path: 'x' };
    return db.uploadPartnerFile(cur.id, file);
  };

  // ── partner CRUD ──
  const savePartner = async (fields, item) => {
    if (mock) {
      const m = mockRef.current;
      if (item) Object.assign(m.partners.find(p => p.id === item.id), fields);
      else { const p = { id: 'p' + Date.now(), assets: [], ...fields }; m.partners.push(p); setSel(p.id); }
      await refreshPartners(); return;
    }
    const row = await db.savePartner(fields, item?.id);
    await refreshPartners();
    if (!item) setSel(row.id);
    say(item ? 'บันทึกข้อมูลแบรนด์แล้ว' : `เพิ่มแบรนด์ ${row.supplier_name} แล้ว — เพิ่มผู้ติดต่อ / บันทึกต่อได้เลย`);
  };
  const deletePartner = async (item) => {
    if (mock) { const m = mockRef.current; m.partners = m.partners.filter(p => p.id !== item.id); setSel(null); await refreshPartners(); return; }
    await db.deletePartner(item.id); setSel(null); await refreshPartners(); say('ลบแบรนด์แล้ว');
  };
  // ⚠️ assets/order_cycles เป็น jsonb ทั้งก้อน — savePartner เขียนทับ ไม่ merge
  // ถ้าใช้ค่าจาก closure (cur) ที่ค้างมาตั้งแต่ก่อน await (เช่นระหว่างอัปไฟล์ใหญ่หลายวินาที)
  // การแก้ที่เกิดระหว่างนั้นจะถูกเขียนทับหายเงียบ → อ่านแถวล่าสุดจาก DB ก่อนเสมอ แล้วค่อยแก้
  const saveJsonList = async (field, mutate) => {
    const pid = cur.id;
    if (mock) {
      const row = mockRef.current.partners.find(p => p.id === pid);
      row[field] = mutate(row[field] ?? []);
      await refreshPartners();
      return;
    }
    const fresh = (await db.listPartners()).find(p => p.id === pid);
    if (!fresh) throw new Error('แบรนด์นี้ถูกลบไปแล้ว — โหลดหน้าใหม่อีกครั้ง');
    await db.savePartner({ [field]: mutate(fresh[field] ?? []) }, pid);
    await refreshPartners();
  };
  const saveAssets = (mutate) => saveJsonList('assets', mutate);
  const saveOneAsset = async (a) =>
    saveAssets(list => list.some(x => x.id === a.id) ? list.map(x => x.id === a.id ? a : x) : [...list, a]);
  const onAssetFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file || !cur) return;
    setDetailBusy(true);
    try { const it = await uploadFor(file); await saveAssets(list => [...list, { ...it, kind: assetKind.current }]); say('แนบไฟล์แล้ว'); }
    catch (er) { setActMsg(er.message ?? String(er)); }
    setDetailBusy(false);
  };

  // ── contact / note CRUD ──
  const saveContact = async (fields, item) => {
    if (mock) { const m = mockRef.current; if (item) Object.assign(m.contacts.find(c => c.id === item.id), fields); else m.contacts.push({ id: 'k' + Date.now(), partner_id: cur.id, ...fields }); await loadDetail(cur); return; }
    await db.savePartnerContact({ ...fields, partner_id: cur.id }, item?.id); await loadDetail(cur); say(item ? 'แก้ผู้ติดต่อแล้ว' : 'เพิ่มผู้ติดต่อแล้ว');
  };
  const deleteContact = async (item) => {
    if (mock) { const m = mockRef.current; m.contacts = m.contacts.filter(c => c.id !== item.id); await loadDetail(cur); return; }
    await db.deletePartnerContact(item.id); await loadDetail(cur);
  };
  const reminderTitle = (fields) => `${kindOf(fields.kind).t}: ${fields.title} — ${cur.supplier_name}`;
  const saveNote = async (fields, { remind }, item) => {
    if (mock) {
      const m = mockRef.current;
      let row;
      if (item) { row = m.notes.find(n => n.id === item.id); Object.assign(row, fields); }
      else { row = { id: 'n' + Date.now(), partner_id: cur.id, ...fields }; m.notes.push(row); }
      const ex = m.reminders.find(r => r.note_id === row.id);
      const closeIt = !!fields.summary && (!fields.held_at || fields.held_at <= new Date().toISOString());   // จดสรุปของนัดที่ผ่านแล้ว = จบนัด
      if (remind) {
        const f = { title: reminderTitle(fields), due_date: dateOnly(fields.held_at), due_time: timeOnly(fields.held_at), partner_id: cur.id, note_id: row.id, assignee: meId, done: closeIt };
        if (ex) Object.assign(ex, f); else m.reminders.push({ id: 'r' + Date.now(), ...f });
      } else if (ex) m.reminders = m.reminders.filter(r => r.id !== ex.id);
      await loadDetail(cur); return;
    }
    const row = await db.savePartnerNote({ ...fields, partner_id: cur.id }, item?.id);
    const ex = reminders.find(r => r.note_id === row.id);
    const closeIt = !!fields.summary && (!fields.held_at || fields.held_at <= new Date().toISOString());   // จดสรุปของนัดที่ผ่านแล้ว = จบนัด
    if (remind) {
      const f = { title: reminderTitle(fields), dueDate: dateOnly(fields.held_at), dueTime: timeOnly(fields.held_at), partnerId: cur.id, noteId: row.id };
      if (ex) await db.updateReminder(ex.id, f); else await db.addReminder({ ...f, assignee: meId });
      if (closeIt) { const cur2 = ex ?? (await db.listPartnerReminders(cur.id)).find(r => r.note_id === row.id); if (cur2 && !cur2.done) await db.setReminderDone(cur2.id, true); }
    } else if (ex) await db.deleteReminder(ex.id);
    await loadDetail(cur);
    say(closeIt && (remind || ex) ? 'บันทึกสรุปแล้ว — ปิดนัดนี้ในแท็บ “วันนี้” ให้แล้ว' : remind ? 'บันทึกแล้ว + ลงปฏิทิน/เตือนในแท็บ “วันนี้”' : 'บันทึกแล้ว');
  };
  const deleteNote = async (item) => {
    if (mock) { const m = mockRef.current; m.notes = m.notes.filter(n => n.id !== item.id); m.reminders = m.reminders.filter(r => r.note_id !== item.id); await loadDetail(cur); return; }
    const ex = reminders.find(r => r.note_id === item.id);
    if (ex) await db.deleteReminder(ex.id);
    await db.deletePartnerNote(item.id); await loadDetail(cur);
  };

  // ── reminders ของแบรนด์ (ตาราง reminder เดียวกับแท็บ "วันนี้") ──
  const addRem = async (e) => {
    e.preventDefault();
    if (!remF.title.trim()) return;
    if (mock) { mockRef.current.reminders.push({ id: 'r' + Date.now(), partner_id: cur.id, title: remF.title, due_date: remF.date || null, due_time: remF.time || null, note: remF.note || null, assignee: remF.assignee || null, done: false }); }
    else {
      try { await db.addReminder({ title: remF.title, dueDate: remF.date, dueTime: remF.time, note: remF.note, partnerId: cur.id, assignee: remF.assignee || null }); }
      catch (er) { setActMsg(er.message ?? String(er)); return; }
    }
    setRemF({ title: '', date: '', time: '', note: '', assignee: '' }); setRemOpen(false); await loadDetail(cur);
    say(remF.date ? 'เพิ่มงานแล้ว — ขึ้นแท็บ “วันนี้” และปฏิทิน' : 'เพิ่มงานแล้ว — ดูได้ที่แท็บ “วันนี้”');
  };
  const toggleRem = async (r, done) => {
    if (mock) { r.done = done; await loadDetail(cur); return; }
    try { await db.setReminderDone(r.id, done); await loadDetail(cur); } catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const deleteRem = async (r) => {
    if (mock) { mockRef.current.reminders = mockRef.current.reminders.filter(x => x.id !== r.id); await loadDetail(cur); return; }
    try { await db.deleteReminder(r.id); await loadDetail(cur); } catch (er) { setActMsg(er.message ?? String(er)); }
  };

  // ── รอบสั่งซื้อ / วันสำคัญประจำปี (เก็บใน supplier_profile.order_cycles) ──
  const saveCycles = (mutate) => saveJsonList('order_cycles', mutate);
  const cycles = cur?.order_cycles ?? [];
  const onSaveCycle = async (row) => {
    const prev = cycles.find(c => c.id === row.id);
    const merged = { ...row, reminder_id: prev?.reminder_id ?? null };
    setCycOpen(false); setCycEdit(null);
    try {
      await saveCycles(list => list.some(c => c.id === row.id) ? list.map(c => c.id === row.id ? merged : c) : [...list, merged]);
      // มีงานเตือนผูกอยู่และยังไม่เสร็จ → ขยับวัน/หัวข้อให้ตรงกับที่แก้
      const ex = cycleRem(merged);
      if (ex && !ex.done) {
        const { title, dueDate, note } = cycleRemFields(merged);
        if (mock) Object.assign(ex, { title, due_date: dueDate, note });
        else await db.updateReminder(ex.id, { title, dueDate, note });
        await loadDetail(cur);
        say('บันทึกแล้ว — ขยับวันเตือนที่ตั้งไว้ให้ตรงกับรอบใหม่แล้ว');
      } else say('บันทึกรอบสั่งซื้อแล้ว — กด “ตั้งเตือน” เพื่อให้ขึ้นแท็บ “วันนี้”');
    } catch (er) { setActMsg(er.message ?? String(er)); }
  };
  const onDeleteCycle = async (row) => {
    const ex = cycleRem(row);
    try {
      if (ex) {
        if (mock) mockRef.current.reminders = mockRef.current.reminders.filter(r => r.id !== ex.id);
        else await db.deleteReminder(ex.id);
      }
      await saveCycles(list => list.filter(c => c.id !== row.id));
      await loadDetail(cur);
      say(ex ? 'ลบรอบนี้และงานเตือนที่ผูกไว้แล้ว' : 'ลบรอบนี้แล้ว');
    } catch (er) { setActMsg(er.message ?? String(er)); }
  };
  // งานเตือนของรอบนี้ — ผูกด้วย id ที่เก็บไว้ในแถว (แก้ชื่อรอบ/ชื่อแบรนด์แล้วยังตามเจอ)
  const cycleRem = (row) => (row.reminder_id ? reminders.find(r => r.id === row.reminder_id) : null) ?? null;
  const cycleRemFields = (row) => {
    const { target, remind } = cycleDates(row);
    const today = todayBkk();
    return {
      title: cycleTitle(row, cur.supplier_name),
      dueDate: remind < today ? today : remind,          // เลยวันเตือนแล้ว = เตือนวันนี้ (ไม่ตั้งย้อนหลัง)
      note: [`ถึงกำหนด ${fmtYmd(target)}`, row.note].filter(Boolean).join(' · '),
      target,
    };
  };
  const setCycleReminder = async (row) => {
    const { title, dueDate, note, target } = cycleRemFields(row);
    let newId;
    if (mock) {
      newId = 'r' + Date.now();
      mockRef.current.reminders.push({ id: newId, partner_id: cur.id, title, due_date: dueDate, due_time: null, note, assignee: meId, done: false });
    } else {
      try {
        const created = await db.addReminder({ title, dueDate, note, partnerId: cur.id, assignee: meId || null });
        newId = created?.id;
      } catch (er) { setActMsg(er.message ?? String(er)); return; }
    }
    try { await saveCycles(list => list.map(c => c.id === row.id ? { ...c, reminder_id: newId ?? null } : c)); }
    catch (er) { setActMsg(er.message ?? String(er)); }
    await loadDetail(cur);
    say(`ตั้งเตือนแล้ว — เตือนวันที่ ${fmtYmd(dueDate)} · ถึงกำหนดจริง ${fmtYmd(target)} · เป็นงานของคุณในแท็บ “วันนี้”`);
  };

  const zipAll = async () => {
    if (mock) { say('โหมดตัวอย่าง — ของจริงจะรวมไฟล์ทุกตู้ของแบรนด์นี้เป็น ZIP โฟลเดอร์ต่อตู้'); return; }
    setZipBusy(true);
    try {
      const codes = Object.fromEntries(ships.map(s => [s.id, s.code]));
      const n = await db.buildPartnerPacket(cur, docs, codes);
      say(`รวมเอกสาร ${n} ไฟล์เป็น ZIP แล้ว`);
    } catch (er) { setActMsg(er.message ?? String(er)); }
    setZipBusy(false);
  };

  // ── derived สำหรับหน้าแบรนด์ ──
  const filtered = partners.filter(p => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [p.supplier_name, p.company_name, ...(p.aliases ?? []), p.handler].some(v => (v || '').toLowerCase().includes(q));
  }).sort((a, b) => Number(a.is_active === false) - Number(b.is_active === false));
  const myVms = cur ? vms.filter(v => (shipsOf[cur.id] ?? []).some(s => s.id === v.dbId)) : [];
  const activeVms = myVms.filter(v => !db.isDone(v.raw?.status) && v.raw?.status !== 'void');
  const productN = cur ? db.partnerNames(cur).reduce((n, a) => n + (productCounts[db.normName(a)] || 0), 0) : 0;
  const openRems = reminders.filter(r => !r.done);
  const doneRems = reminders.filter(r => r.done);
  const nowIso = new Date().toISOString();
  const nextMeeting = notes.filter(n => (n.kind === 'meeting' || n.kind === 'call') && n.held_at && n.held_at >= nowIso)
    .sort((a, b) => a.held_at.localeCompare(b.held_at))[0] ?? null;
  const nextRem = openRems.filter(r => r.due_date).sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))[0] ?? null;
  const overdue = openRems.filter(r => r.due_date && String(r.due_date).slice(0, 10) < dayKey(new Date()));
  const shownNotes = notes.filter(n => noteFilter === 'all' || n.kind === noteFilter);
  const docCats = [...new Set(docs.map(d => reqCat(d.req_type)))];
  const shownDocs = docs.filter(d => docFilter === 'all' || reqCat(d.req_type) === docFilter);
  const docsByShip = useMemo(() => {
    const m = new Map();
    for (const d of shownDocs) { if (!m.has(d.shipment_id)) m.set(d.shipment_id, []); m.get(d.shipment_id).push(d); }
    // ตู้ที่กำลังทำแต่ยังไม่มีไฟล์เลย ก็ต้องโผล่ (จะได้เห็นว่าขาดอะไร)
    if (cur && docFilter === 'all') for (const s of shipsOf[cur.id] ?? []) if (!db.isDone(s.status) && s.status !== 'void' && !m.has(s.id)) m.set(s.id, []);
    // เรียงตู้ใหม่→เก่า ตาม eta
    return [...m.entries()].map(([sid, list]) => ({ vm: vms.find(v => v.dbId === sid), ship: ships.find(s => s.id === sid), list }))
      .sort((a, b) => String(b.ship?.eta || '').localeCompare(String(a.ship?.eta || '')));
  }, [shownDocs, vms, ships, cur?.id, docFilter, shipsOf]);
  const facts = cur ? FACTS.map(([k, label, type]) => ({ k, label, type, v: cur[k] })).filter(x => x.v !== null && x.v !== undefined && x.v !== '') : [];
  const CAT_TH = { money: 'เงิน/ใบแจ้งหนี้', ship: 'ขนส่ง', origin: 'Form สิทธิ', permit: 'ใบอนุญาต', customs: 'ศุลกากร', wh: 'คลัง' };
  const todayKey = dayKey(new Date());
  // ค่าล่าสุดจากตู้จริง (ไม่ต้องพิมพ์): incoterm · สกุลเงิน · ประเทศต้นทาง ของ consignment ล่าสุด
  const latestCons = (() => {
    if (!cur) return null;
    const sorted = [...(shipsOf[cur.id] ?? [])].sort((a, b) => String(b.eta || b.created_at || '').localeCompare(String(a.eta || a.created_at || '')));
    for (const s of sorted) { const c = (s.consignment ?? []).find(x => db.partnerMatches(cur, x.supplier_name)); if (c) return { ...c, code: s.code }; }
    return null;
  })();
  // consignment ของแบรนด์นี้ (ใช้เทียบใบสั่งซื้อ ↔ ของที่มาถึง) + ป้ายชื่อตู้สำหรับแสดงผล
  const myCons = useMemo(() => {
    if (!cur) return [];
    return (shipsOf[cur.id] ?? []).flatMap(sh =>
      (sh.consignment ?? []).filter(c => db.partnerMatches(cur, c.supplier_name))
        .map(c => ({ id: c.id, code: sh.code, eta: sh.eta, invoice_no: c.invoice_no })));
  }, [cur?.id, shipsOf]);
  const myConsIds = useMemo(() => myCons.map(c => c.id), [myCons]);
  const consLabel = (id) => {
    const c = myCons.find(x => x.id === id);
    return c ? `${c.code}${c.invoice_no ? ` · INV ${c.invoice_no}` : ''}` : 'ตู้';
  };

  // เอกสารบังคับที่ยังขาด ต่อตู้ที่กำลังทำ (จาก document_requirement ที่โหลดมากับตู้แล้ว)
  const missingByShip = (() => {
    const m = {};
    if (!cur) return m;
    for (const s of shipsOf[cur.id] ?? []) {
      if (db.isDone(s.status) || s.status === 'void') continue;
      const myCons = new Set((s.consignment ?? []).filter(c => db.partnerMatches(cur, c.supplier_name)).map(c => c.id));
      const miss = (s.document_requirement ?? []).filter(r => r.required && ['missing', 'flagged'].includes(r.status) && (!r.consignment_id || myCons.has(r.consignment_id)));
      if (miss.length) m[s.id] = miss;
    }
    return m;
  })();
  // "ทำต่อจากตรงนี้" — คำนวณจากของจริง ไม่ต้องพิมพ์
  const heldNoMinutes = notes.filter(n => (n.kind === 'meeting' || n.kind === 'call') && n.held_at && n.held_at < nowIso && !n.summary);
  const in7 = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString(); })();
  const soonMeetings = notes.filter(n => (n.kind === 'meeting' || n.kind === 'call') && n.held_at && n.held_at >= nowIso && n.held_at <= in7);
  const dueToday = openRems.filter(r => String(r.due_date || '').slice(0, 10) === todayKey);
  const nextActions = cur ? [
    ...overdue.map(r => ({ k: 'late', t: `เลยกำหนด ${fmtDue(r)} — ${r.title}` })),
    ...dueToday.map(r => ({ k: 'today', t: `วันนี้${r.due_time ? ' ' + r.due_time : ''} — ${r.title}` })),
    ...heldNoMinutes.map(n => ({ k: 'minutes', t: `${kindOf(n.kind).t} ${fmtDT(n.held_at)} ผ่านไปแล้ว — ยังไม่จดสรุป: ${n.title}`, go: () => setModal({ t: 'note', item: n }) })),
    ...soonMeetings.map(n => ({ k: 'soon', t: `${kindOf(n.kind).t} ${fmtDT(n.held_at)} — ${n.title}`, go: () => setModal({ t: 'note', item: n }) })),
    ...activeVms.filter(v => v.urgency === 'urgent' || v.urgency === 'watch').map(v => ({ k: v.urgency, t: `ตู้ ${v.id} — ${v.why}`, go: () => onOpenShipment(v.id) })),
    ...Object.entries(missingByShip).map(([sid, miss]) => { const s = ships.find(x => x.id === sid); return { k: 'docs', t: `ตู้ ${s?.code} ยังขาด ${miss.length} ใบ — ${miss.slice(0, 2).map(r => reqLabel(r.req_type)).join(', ')}${miss.length > 2 ? ' …' : ''}`, go: () => onOpenShipment(s?.code) }; }),
    ...(contacts.length && !contacts.some(c => c.is_primary) ? [{ k: 'gap', t: 'ยังไม่ได้ตั้งผู้ติดต่อหลัก — เปิดผู้ติดต่อแล้วติ๊ก ★', go: () => setModal({ t: 'contact', item: contacts[0] }) }] : []),
    // ไฟล์ที่มีวันหมดอายุ (price list / สัญญา) — หมดแล้ว หรือเหลือ ≤ 30 วัน
    ...(cur.assets ?? []).map(a => ({ a, st: assetState(a) })).filter(x => x.st && x.st.level !== 'ok')
      .sort((x, y) => x.st.d - y.st.d)
      .map(({ a, st }) => ({ k: st.level === 'exp' ? 'late' : 'minutes',
        t: st.level === 'exp' ? `${a.name} หมดอายุแล้ว ${-st.d} วัน — ขอไฟล์ใหม่` : `${a.name} เหลืออีก ${st.d} วันจะหมดอายุ`,
        btn: 'แก้ไฟล์ ✎', go: () => setModal({ t: 'asset', item: a }) })),
    // รอบสั่งซื้อที่ใกล้ถึง (หรือเข้าช่วงเตือนแล้ว) แต่ยังไม่ได้ตั้งเตือน
    ...(cur.order_cycles ?? []).map(row => { const c = cycleDates(row); return { row, dT: daysUntilBkk(c.target), dR: daysUntilBkk(c.remind) }; })
      .filter(x => x.dT >= 0 && x.dR <= 45 && !cycleRem(x.row))
      .sort((a, b) => a.dT - b.dT)
      .map(({ row, dT, dR }) => ({ k: dR <= 0 ? 'minutes' : 'soon',
        t: dR <= 0
          ? `${row.label} — ${cycleText(row)} อีก ${dT} วัน ถึงเวลาเตือนแล้วแต่ยังไม่ได้ตั้ง`
          : `${row.label} — ${cycleText(row)} (อีก ${dR} วันถึงวันเตือน) ยังไม่ได้ตั้งเตือน`,
        btn: 'ตั้งเตือน', go: () => setCycleReminder(row) })),
  ] : [];
  // ความครบของสมุดส่งมอบ n/8 — สิ่งที่คนใหม่ต้องมีเพื่อทำงานต่อได้
  const checklist = cur ? [
    { ok: !!cur.brand_profile, t: 'ภาพรวมแบรนด์', go: () => setModal({ t: 'partner', item: cur }) },
    { ok: contacts.some(c => c.is_primary), t: 'ผู้ติดต่อหลัก', go: () => setModal({ t: 'contact', item: contacts.find(c => c.is_primary) ?? null }) },
    { ok: !!(cur.order_channel || cur.order_email), t: 'วิธี/ช่องทางสั่งซื้อ', go: () => setModal({ t: 'partner', item: cur }) },
    { ok: (cur.order_cycles ?? []).length > 0 || !!cur.order_cycle, t: 'รอบสั่งซื้อ', go: () => setCycOpen(true) },
    { ok: !!cur.payment_terms, t: 'เงื่อนไขชำระเงิน', go: () => setModal({ t: 'partner', item: cur }) },
    { ok: cur.lead_time_days != null && cur.lead_time_days !== '', t: 'Lead time', go: () => setModal({ t: 'partner', item: cur }) },
    { ok: !!(cur.ship_from || cur.forwarder), t: 'ส่งจาก / forwarder', go: () => setModal({ t: 'partner', item: cur }) },
    { ok: !!(cur.key_facts || cur.quirks), t: 'สิ่งที่ต้องรู้ / ระวัง', go: () => setModal({ t: 'partner', item: cur }) },
  ] : [];
  const checkN = checklist.filter(c => c.ok).length;

  // ── อัลบั้มเอกสารต่อตู้: ช่องมาตรฐาน 8 ช่อง + ไฟล์ส่วนเกิน ──
  // ⚠️ สร้างช่องจาก docs "ดิบ" เสมอ (ไม่ใช่ shownDocs) — กดกรองหมวดแล้วผังช่องต้องไม่เปลี่ยนรูป
  // ⚠️ ช่องที่ขาดอ่านจาก missingByShip อย่างเดียว (มันข้ามตู้ที่ปิดงาน/ยกเลิกให้แล้ว) จะได้ไม่ทวงตู้ที่จบไปแล้ว
  const albums = useMemo(() => {
    if (!cur) return [];
    const byShip = new Map();
    for (const d of docs) { if (!byShip.has(d.shipment_id)) byShip.set(d.shipment_id, []); byShip.get(d.shipment_id).push(d); }
    return (shipsOf[cur.id] ?? []).map(sh => {
      const list = byShip.get(sh.id) ?? [];
      const miss = missingByShip[sh.id] ?? [];
      const vm = vms.find(v => v.dbId === sh.id);
      const closed = db.isDone(sh.status) || sh.status === 'void';
      let hot = 0;
      const slots = SLOTS.map((def, i) => {
        const files = list.filter(d => def.t.includes(d.req_type))
          .sort((a, b) => String(b.uploaded_at || '').localeCompare(String(a.uploaded_at || '')));
        const req = miss.find(r => def.t.includes(r.req_type));
        const state = files.length ? (req?.status === 'flagged' ? 'flag' : 'ok') : (req ? 'miss' : 'na');
        // กะพริบเฉพาะช่องที่ "ขาดจริงและบล็อกขั้นตอนถัดไป" ของตู้ที่เร่งเท่านั้น และไม่เกิน 2 ช่อง
        const urgent = state === 'miss' && !closed && vm?.urgency === 'urgent'
          && BLOCKING_BY.includes(req?.needed_by) && hot < 2 && ++hot;
        return { i, ...def, files, req, state, cat: reqCat(def.t[0]), urgent: !!urgent };
      });
      const extras = list.filter(d => !SLOT_TYPES.has(d.req_type));
      const have = slots.filter(x => x.state === 'ok').length;
      const need = slots.filter(x => x.state !== 'na').length;
      const flagged = slots.some(x => x.state === 'flag');
      const gaps = slots.filter(x => x.state === 'miss').length;
      const inv = [...new Set((sh.consignment ?? [])
        .filter(c => db.partnerMatches(cur, c.supplier_name) && c.invoice_no).map(c => c.invoice_no))];
      return { sh, vm, closed, slots, extras, files: list, have, need, gaps, flagged, inv,
               complete: need > 0 && have === need && !flagged };
    }).sort((a, b) => String(b.sh.eta || '').localeCompare(String(a.sh.eta || '')));
  }, [cur?.id, docs, shipsOf, vms, missingByShip]);

  // ค้นหา: ตรงที่ระดับตู้ = โชว์ทั้งแผ่น · ตรงที่ระดับไฟล์ = กางแผ่นนั้นให้เอง
  const q = docQ.trim().toLowerCase();
  const shownAlbums = useMemo(() => albums.map(a => {
    const shipHit = !q || [a.sh.code, a.vm?.displayName, a.sh.label, ...a.inv]
      .some(v => String(v ?? '').toLowerCase().includes(q));
    const hitFiles = q ? a.files.filter(d =>
      String(d.file_name ?? '').toLowerCase().includes(q) || reqLabel(d.req_type).toLowerCase().includes(q)) : [];
    return { ...a, shipHit, hitFiles, match: shipHit || hitFiles.length > 0 };
  }).filter(a => a.match), [albums, q]);

  const scored = albums.filter(a => a.need > 0);
  const completeN = scored.filter(a => a.complete).length;
  const gapTotal = albums.reduce((n, a) => n + a.gaps, 0);
  const catCount = docs.reduce((m, d) => { const c = reqCat(d.req_type); m[c] = (m[c] || 0) + 1; return m; }, {});
  const hitFileN = q ? shownAlbums.reduce((n, a) => n + (a.shipHit ? a.files.length : a.hitFiles.length), 0) : docs.length;
  const isOpen = (a) => openShip[a.sh.id] ?? (q ? true : !a.closed);


  if (st.loading) return <div className="placeholder"><h2>กำลังโหลดแบรนด์…</h2></div>;
  if (st.pending) return <DbPending onRetry={() => setNonce(n => n + 1)}/>;
  if (st.error) return (
    <div className="placeholder"><h2>โหลดแบรนด์ไม่สำเร็จ</h2><p className="mono">{st.error}</p>
      <button className="tab" onClick={() => setNonce(n => n + 1)}>ลองใหม่</button></div>
  );

  const hue = hueOf(cur);
  const seedN = unmatched.length + unmatchedAliases.length;

  return (
    <div className="page bh">
      <input ref={assetFile} type="file" style={{ display: 'none' }} onChange={onAssetFile} accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.ai,.psd,.txt,.csv"/>
      <div className="page-h">
        <h2>แบรนด์ & ซัพพลายเออร์</h2>
        <span className="meta">ศูนย์ข้อมูลคู่ค้า</span>
        <input className="search" placeholder="ค้นชื่อแบรนด์ / บริษัท / ชื่อสะกดอื่น…" value={search} onChange={e => setSearch(e.target.value)}/>
        {seedN > 0 && partners.length > 0 && (
          <button className="btn2" onClick={seed} disabled={detailBusy} title={unmatched.join(', ')}>สร้างจากตู้/คลังที่มี ({seedN})</button>
        )}
        <button className="btn1" onClick={() => setModal({ t: 'partner', item: null })}>＋ เพิ่มแบรนด์</button>
      </div>
      {st.demo && <div className="importmsg">โหมดตัวอย่าง — ข้อมูลจำลองไว้ดูหน้าตา/ลองกด (ของจริงเห็นหลัง login)</div>}
      {flash && <div className="flash">{flash}</div>}
      {actMsg && <div className="actbar"><span>{actMsg}</span><button className="actbar-x" onClick={() => setActMsg(null)}>✕</button></div>}

      <div className="classlayout">
        <aside className="brandrail">
          <div className="brl-h">แบรนด์ / บริษัท ({partners.length})</div>
          {filtered.map(p => {
            const n = (shipsOf[p.id] ?? []).length;
            return (
              <button key={p.id} className={`brl-item ${sel === p.id ? 'on' : ''} ${p.is_active === false ? 'bh-inactive' : ''}`} onClick={() => setSel(p.id)}>
                <span className="bh-dot" style={{ background: hueOf(p) }}/>
                <span className="brl-n">{p.supplier_name}{p.is_active === false ? ' · เลิกใช้แล้ว' : ''}</span>
                {n > 0 && <span className="brl-c" title="จำนวนตู้">{n}</span>}
              </button>
            );
          })}
          {!filtered.length && <div className="brl-empty">{partners.length ? 'ไม่พบชื่อที่ค้น' : 'ยังไม่มีแบรนด์'}</div>}
        </aside>

        <div className="classmain">
          {!partners.length ? (
            <div className="firststep">
              <div className="fs-t">เริ่มศูนย์ข้อมูลแบรนด์ใน 1 นาที</div>
              <div className="fs-steps">
                <div className="fs-step"><b>①</b>กด “สร้างจากตู้/คลังที่มี” — ระบบตั้งแบรนด์ให้จาก supplier ในตู้และแบรนด์สินค้าในคลัง{seedN ? ` (พบ ${seedN} ชื่อ)` : ''} ไม่ต้องพิมพ์เอง</div>
                <div className="fs-step"><b>②</b>เปิดแต่ละแบรนด์ → กด “✎ แก้ข้อมูล” ใส่วิธีสั่งซื้อ / เงื่อนไข / สิ่งที่ต้องรู้ แล้วเพิ่มผู้ติดต่อ</div>
                <div className="fs-step"><b>③</b>ทุกครั้งที่ประชุม/คุยกับเจ้านั้น จดไว้ที่ “ประชุม & บันทึก” — ติ๊กเตือน ระบบจะขึ้นแท็บ “วันนี้” และปฏิทินให้</div>
              </div>
              {seedN > 0
                ? <button className="btn1" onClick={seed} disabled={detailBusy}>{detailBusy ? 'กำลังสร้าง…' : `สร้างจากตู้/คลังที่มี (${seedN})`}</button>
                : <button className="btn1" onClick={() => setModal({ t: 'partner', item: null })}>＋ เพิ่มแบรนด์แรก</button>}
              <div className="fs-hint">เอกสาร PI / Invoice / Packing List / B/L ของทุกตู้จะถูกดึงมารวมไว้ที่หน้าแบรนด์เองจากที่บันทึกไว้ใน Console — ไม่ต้องอัปซ้ำ</div>
            </div>
          ) : !cur ? (
            <div className="placeholder"><h2>เลือกแบรนด์ทางซ้าย</h2><p>หรือกด “＋ เพิ่มแบรนด์” เพื่อสร้างใหม่</p></div>
          ) : (
            <div className="bh-page" style={{ '--bh': hue }}>
              {/* ── หัวแบรนด์ ── */}
              <div className="bh-head">
                <div className="bh-bar"/>
                <div className="bh-head-bd">
                  <div className="bh-h1">
                    <span className="bh-name">{cur.supplier_name}</span>
                    {cur.company_name && <span className="bh-co">{cur.company_name}</span>}
                    {cur.country && <span className="pill">{countryTh(cur.country)}</span>}
                    {cur.is_active === false && <span className="pill">เลิกใช้แล้ว</span>}
                    <button className="pill editpill" onClick={() => setModal({ t: 'partner', item: cur })}>✎ แก้ข้อมูล</button>
                    <button className="pill editpill" onClick={() => window.print()}
                      title="พิมพ์สรุปข้อมูลแบรนด์นี้ (A4 · ยาวตามข้อมูลที่มี) — ใช้ส่งต่องาน/เข้าประชุม">⎙ พิมพ์สรุปแบรนด์</button>
                    <span className="bh-signer noprint"><SignerEditor signer={signer} onSaved={reloadSigner}/></span>
                  </div>
                  {cur.brand_profile
                    ? <div className="bh-profile">{cur.brand_profile}</div>
                    : <button className="bh-profile empty" onClick={() => setModal({ t: 'partner', item: cur })}>＋ ใส่ภาพรวม / character ของแบรนด์ — แบรนด์นี้คืออะไร ขายอะไร จุดเด่น กลุ่มลูกค้า</button>}
                  <div className="bh-chips">
                    <span className="schip">ตู้ <b>{myVms.length}</b>{activeVms.length ? <em> · กำลังทำ {activeVms.length}</em> : null}</span>
                    <span className="schip">เอกสาร <b>{docs.length}</b></span>
                    {productN > 0 && <span className="schip">สินค้าในคลัง <b>{productN.toLocaleString()}</b></span>}
                    {cur.handler && <span className="schip">ผู้ดูแล <b>{cur.handler}</b></span>}
                    {latestCons && (latestCons.incoterm || latestCons.currency || latestCons.origin_country) && (
                      <span className="schip" title={`จาก consignment ล่าสุด ${latestCons.code} — ไม่ต้องพิมพ์`}>ล่าสุดจากตู้ <b>{[latestCons.incoterm, latestCons.currency, latestCons.origin_country].filter(Boolean).join(' · ')}</b></span>
                    )}
                    {(cur.aliases ?? []).length > 0 && <span className="bh-aliases" title="ชื่อสะกดอื่นที่ผูกไว้">ชื่ออื่น: {cur.aliases.join(' · ')}</span>}
                    {cur.updated_at && <span className="bh-aliases">อัปเดต {fmtD(cur.updated_at)}</span>}
                  </div>
                  {/* สมุดส่งมอบครบแค่ไหน — กดชิปที่ยังขาดเพื่อไปเติม */}
                  <div className="bh-meter">
                    <span className="bh-meter-l">ข้อมูลครบ <b>{checkN}/{checklist.length}</b></span>
                    <span className="bh-meter-bar"><i style={{ width: `${Math.round(checkN / Math.max(1, checklist.length) * 100)}%` }}/></span>
                    {checklist.filter(c => !c.ok).map(c => <button key={c.t} className="bh-gap" onClick={c.go}>＋ {c.t}</button>)}
                    {checkN === checklist.length && <span className="bh-meter-ok">✓ ครบ</span>}
                  </div>
                  {/* ทำต่อจากตรงนี้ — คำนวณจากของจริง (งานเลยกำหนด · วันนี้ · นัดที่ยังไม่จดสรุป · นัดใน 7 วัน · ตู้ร้อน · เอกสารขาด) */}
                  <div className={`bh-next ${overdue.length || heldNoMinutes.length ? 'warn' : ''}`}>
                    <span className="lb">ทำต่อจากตรงนี้</span>
                    {nextActions.length ? (
                      <div className="bh-next-list">
                        {nextActions.slice(0, 5).map((a, i) => (
                          <div className={`bh-next-row k-${a.k}`} key={i}>
                            <span className="tx">{a.t}</span>
                            {a.go && <button className="t-go sm" onClick={a.go}>{a.btn ?? (a.k === 'minutes' ? 'จดสรุป ✎' : a.k === 'urgent' || a.k === 'watch' || a.k === 'docs' ? 'เปิดตู้ →' : 'เปิด →')}</button>}
                          </div>
                        ))}
                        {nextActions.length > 5 && <div className="bh-next-more">+ อีก {nextActions.length - 5} รายการ</div>}
                      </div>
                    ) : nextRem ? (
                      <span className="tx">ถัดไป {fmtDue(nextRem)} — {nextRem.title}</span>
                    ) : (
                      <span className="tx muted">ไม่มีอะไรค้าง — เพิ่มงานได้ที่ “งานที่ต้องทำ” หรือ “ประชุม & บันทึก”</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bh-grid">
                {/* ── ซ้าย: ติดต่อ / เงื่อนไข / ผู้ติดต่อ / ต้องรู้ ── */}
                <div className="bh-col">
                  <div className="bh-card">
                    <div className="bh-card-h"><span className="bh-card-t">ติดต่อ & เงื่อนไขการสั่งซื้อ</span>
                      <button className="mini" onClick={() => setModal({ t: 'partner', item: cur })}>✎ แก้</button></div>
                    {facts.length ? (
                      <dl className="bh-facts">
                        {facts.map(x => (
                          <div className="bh-fact" key={x.k}>
                            <dt>{x.label}</dt>
                            <dd>{x.type === 'link' ? <a href={/^https?:/i.test(x.v) ? x.v : `https://${x.v}`} target="_blank" rel="noopener">{x.v}</a>
                              : x.type === 'mail' && /@/.test(x.v) ? <a href={`mailto:${x.v}`}>{x.v}</a>
                              : x.type === 'days' ? `${x.v} วัน` : x.v}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : <div className="bh-empty">ยังไม่ได้ใส่ — กด ✎ แก้ เพื่อใส่วิธีสั่งซื้อ / เงื่อนไขชำระ / lead time </div>}
                  </div>

                  <div className="bh-card">
                    <div className="bh-card-h">
                      <span className="bh-card-t">รอบสั่งซื้อ & วันสำคัญประจำปี ({cycles.length})</span>
                      {!cycOpen && cycEdit == null && <button className="mini hot" onClick={() => setCycOpen(true)}>＋ เพิ่มรอบ</button>}
                    </div>
                    <div className="t-hint" style={{ marginTop: 0 }}>ใส่วันที่ของรอบที่เกิดซ้ำทุกปี แล้วกด “ตั้งเตือน” ระบบจะสร้างงานล่วงหน้าให้ในแท็บ “วันนี้” (เป็นงานของคุณ)</div>
                    {cur.order_cycle && <div className="bh-legacy">โน้ตเดิม: {cur.order_cycle} <i>· แก้ได้ที่ ✎ แก้ข้อมูล</i></div>}
                    {cycOpen && cycEdit == null && <CycleForm onSave={onSaveCycle} onCancel={() => setCycOpen(false)}/>}
                    {cycles.map(row => cycEdit === row.id ? (
                      <CycleForm key={row.id} initial={row} onSave={onSaveCycle} onCancel={() => setCycEdit(null)}/>
                    ) : (() => {
                      const { remind, target, lead } = cycleDates(row);
                      const rem = cycleRem(row);
                      return (
                        <div className="bh-cyc" key={row.id}>
                          <div className="bh-cyc-d"><b>{cycleText(row)}</b><span>ทุกปี</span></div>
                          <div className="t-item-tx">
                            <div className="t-item-t">{row.label}</div>
                            <div className="t-item-s">
                              {rem ? `ตั้งเตือนแล้ว ${fmtDue(rem)}`
                                : `ถึงกำหนด ${fmtYmd(target)}${lead ? ` · เตือน ${fmtYmd(remind)} (ล่วงหน้า ${lead} วัน)` : ' · เตือนวันนั้น'}`}
                              {row.note ? ` · ${row.note}` : ''}
                            </div>
                          </div>
                          <div className="t-remacts">
                            {rem && !rem.done ? <span className="bh-remtag">◆ ตั้งเตือนแล้ว</span>
                              : rem ? <button className="mini" title="รอบนี้ทำเสร็จแล้ว — ตั้งเตือนรอบถัดไปได้เลย" onClick={() => setCycleReminder(row)}>✓ ทำแล้ว · ตั้งรอบหน้า</button>
                              : <button className="mini hot" title={`สร้างงานเตือนวันที่ ${fmtYmd(cycleDates(row).remind)} ในแท็บ “วันนี้”`} onClick={() => setCycleReminder(row)}>ตั้งเตือน</button>}
                            <button className="t-iconbtn" title="แก้" onClick={() => { setCycEdit(row.id); setCycOpen(false); setCycDel(null); }}>✎</button>
                            <button className="t-iconbtn del" title="ลบรอบนี้" onClick={() => setCycDel(row.id)}>✕</button>
                          </div>
                          {cycDel === row.id && (
                            <div className="del-confirm bh-cyc-del">
                              <span>ลบรอบ “{row.label}”?{rem ? ` งานเตือน ${fmtDue(rem)} ที่ผูกไว้จะถูกลบด้วย` : ''}</span>
                              <button className="btn-del" onClick={async () => { setCycDel(null); await onDeleteCycle(row); }}>ยืนยันลบ</button>
                              <button className="btn2 sm" onClick={() => setCycDel(null)}>ไม่ลบ</button>
                            </div>
                          )}
                        </div>
                      );
                    })())}
                    {!cycles.length && !cycOpen && (
                      <div className="bh-empty">{cur.order_cycle
                        ? <>จดไว้ว่า “{cur.order_cycle}” — กด “＋ เพิ่มรอบ” เพื่อใส่วันที่จริง จะได้ตั้งเตือนล่วงหน้าได้</>
                        : 'ยังไม่มี — เช่น ปิดรับยอด pre-order 15 ต.ค. · ราคาปรับใหม่ 1 ก.พ.'}</div>
                    )}
                  </div>

                  <div className="bh-card">
                    <div className="bh-card-h"><span className="bh-card-t">ผู้ติดต่อ ({contacts.length})</span>
                      <button className="mini hot" onClick={() => setModal({ t: 'contact', item: null })}>＋ เพิ่มผู้ติดต่อ</button></div>
                    {contacts.length ? (
                      <div className="bh-contacts">
                        {contacts.map(c => (
                          <button className={`bh-contact ${c.is_primary ? 'primary' : ''}`} key={c.id} onClick={() => setModal({ t: 'contact', item: c })}>
                            <div className="bh-ct-top"><span className="bh-ct-name">{c.name}</span>{c.is_primary && <span className="bh-ct-star">★ หลัก</span>}</div>
                            {c.role && <div className="bh-ct-role">{c.role}</div>}
                            <div className="bh-ct-lines">
                              {c.email && <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}>{c.email}</a>}
                              {c.phone && <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()}>{c.phone}</a>}
                              {c.chat_id && <span>{c.chat_id}</span>}
                              {c.languages && <span className="bh-ct-lang">{c.languages}</span>}
                            </div>
                            {c.note && <div className="bh-ct-note">{c.note}</div>}
                          </button>
                        ))}
                      </div>
                    ) : <div className="bh-empty">ยังไม่มีผู้ติดต่อ — ใส่ชื่อคนที่คุยด้วยประจำ + ดูแลเรื่องอะไร จะได้ไม่ต้องถามใครว่า “ส่งหาใคร”</div>}
                  </div>

                  <div className="bh-card">
                    <div className="bh-card-h"><span className="bh-card-t">สิ่งที่ต้องรู้ / ระวัง</span>
                      <button className="mini" onClick={() => setModal({ t: 'partner', item: cur })}>✎ แก้</button></div>
                    {cur.key_facts && (() => {
                      // ข้อมูลที่ตรวจแล้วบางแบรนด์ยาวเป็นพันตัวอักษร — ย่อไว้ก่อนไม่ให้กลบการ์ดอื่น
                      const long = cur.key_facts.length > 700;
                      return (
                        <>
                          <div className={`bh-keyfacts ${long && !kfOpen ? 'clip' : ''}`}>{cur.key_facts}</div>
                          {long && (
                            <button className="bh-kf-more" onClick={() => setKfOpen(!kfOpen)}>
                              {kfOpen ? 'ย่อลง ▲' : `ดูทั้งหมด (${cur.key_facts.split('\n').filter(Boolean).length} บรรทัด) ▼`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {cur.quirks && <div className="bh-quirk"><b>ระวังเป็นพิเศษ:</b> {cur.quirks}</div>}
                    {watchouts.length > 0 && (
                      <div className="watchbox bh-watch">
                        <div className="watch-h">● บทเรียนจากตู้ก่อนๆ ของเจ้านี้ ({watchouts.length}) — จากสมุดบันทึกตู้</div>
                        {watchouts.map((w, i) => (
                          <div className="watch-item" key={i}>
                            <span className="wt">{w.text}</span>
                            <span className="wm">{w.from_shipment_code}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {!cur.key_facts && !cur.quirks && !watchouts.length && <div className="bh-empty">ยังไม่มี — สิ่งที่ถ้าไม่รู้แล้วจะพลาด ใส่ไว้ที่นี่</div>}
                  </div>
                </div>

                {/* ── ขวา: งานที่ต้องทำ / ประชุม & บันทึก ── */}
                <div className="bh-col">
                  <div className="bh-card">
                    <div className="bh-card-h"><span className="bh-card-t">งานที่ต้องทำ / เตือนความจำ ({openRems.length})</span>
                      {!remOpen && <button className="mini hot" onClick={() => { setRemF({ title: '', date: '', time: '', note: '', assignee: meId || '' }); setRemOpen(true); }}>＋ เพิ่มงาน</button>}</div>
                    <div className="t-hint" style={{ marginTop: 0 }}>งานที่มีวันที่จะขึ้นแท็บ “วันนี้” และปฏิทินของแอพให้อัตโนมัติ</div>
                    {remOpen && (
                      <form className="t-remform" onSubmit={addRem}>
                        <input className="t-in" autoFocus placeholder="เช่น ส่งยอด pre-order · ทวง CO · โอนเงินงวด 2" value={remF.title} onChange={e => setRemF({ ...remF, title: e.target.value })}/>
                        <div className="t-remrow">
                          <label className="t-remfield"><span>วันเตือน</span><input className="t-in sm" type="date" value={remF.date} onChange={e => setRemF({ ...remF, date: e.target.value })}/></label>
                          <label className="t-remfield"><span>เวลา</span><input className="t-in sm" type="time" value={remF.time} onChange={e => setRemF({ ...remF, time: e.target.value })}/></label>
                        </div>
                        {staff.length > 0 && (
                          <label className="t-remfield">
                            <span>มอบหมายให้ใคร</span>
                            <select className="t-in sm" value={remF.assignee} onChange={e => setRemF({ ...remF, assignee: e.target.value })}>
                              <option value="">ทั้งทีม (ไม่ระบุคน)</option>
                              {staff.map(s => <option key={s.user_id} value={s.user_id}>{s.name}{s.user_id === meId ? ' (ฉัน)' : ''}</option>)}
                            </select>
                          </label>
                        )}
                        <input className="t-in" placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)" value={remF.note} onChange={e => setRemF({ ...remF, note: e.target.value })}/>
                        <div className="t-rembtns">
                          <button className="t-savebtn" disabled={!remF.title.trim()}>เพิ่มงาน</button>
                          <button type="button" className="t-cancelbtn" onClick={() => setRemOpen(false)}>ยกเลิก</button>
                        </div>
                      </form>
                    )}
                    {openRems.map(r => (
                      <div className={`t-remitem ${r.due_date && String(r.due_date).slice(0, 10) < dayKey(new Date()) ? 'bh-late' : ''}`} key={r.id}>
                        <button className="t-check" title="ทำเสร็จแล้ว" onClick={() => toggleRem(r, true)} aria-label="เสร็จ"/>
                        <div className="t-item-tx">
                          <div className="t-item-t">{r.title}</div>
                          <div className="t-item-s">{[fmtDue(r), r.assignee ? `→ ${staffName(r.assignee) || 'พนักงาน'}` : null, r.note].filter(Boolean).join(' · ')}</div>
                        </div>
                        <div className="t-remacts"><button className="t-iconbtn del" title="ลบ" onClick={() => deleteRem(r)}>✕</button></div>
                      </div>
                    ))}
                    {!openRems.length && !remOpen && <div className="bh-empty">ไม่มีงานค้างของแบรนด์นี้</div>}
                    {doneRems.length > 0 && <div className="t-donetoggle" style={{ cursor: 'default' }}>เสร็จแล้ว {doneRems.length} งาน</div>}
                  </div>

                  <div className="bh-card">
                    <div className="bh-card-h">
                      <span className="bh-card-t">ประชุม & บันทึก ({notes.length})</span>
                      <div className="grp-actions">
                        <button className="mini hot" onClick={() => setModal({ t: 'note', item: null, kind: 'meeting' })}>＋ ประชุม / นัดหมาย</button>
                        <button className="mini" onClick={() => setModal({ t: 'note', item: null, kind: 'note' })}>＋ จดโน้ต</button>
                      </div>
                    </div>
                    <div className="jr-kinds bh-filters">
                      <button className={`jr-kind ${noteFilter === 'all' ? 'on' : ''}`} onClick={() => setNoteFilter('all')}>ทั้งหมด</button>
                      {NOTE_KINDS.map(k => <button key={k.id} className={`jr-kind ${noteFilter === k.id ? 'on' : ''}`} style={noteFilter === k.id ? { borderColor: k.c, color: k.c } : {}} onClick={() => setNoteFilter(k.id)}>{k.t}</button>)}
                    </div>
                    <div className="jr-timeline">
                      {shownNotes.map(n => {
                        const k = kindOf(n.kind);
                        const rem = reminders.find(r => r.note_id === n.id);
                        const upcoming = n.held_at && n.held_at >= nowIso;
                        return (
                          <div className={`jr-note bh-note ${n.pinned ? 'pinned' : ''} ${upcoming ? 'upcoming' : ''}`} key={n.id}>
                            <span className="jr-dot" style={{ background: k.c }}/>
                            <div className="jr-body">
                              <div className="jr-meta">
                                <span className="jr-kindtag" style={{ color: k.c }}>{k.t}</span>
                                {n.held_at && <span className="jr-time">{fmtDT(n.held_at)}</span>}
                                {n.pinned && <span className="jr-watchtag bh-pin">ปักไว้</span>}
                                {rem && !rem.done && <span className="bh-remtag">◆ เตือนแล้ว</span>}
                                <span className="jr-actions"><button className={`jr-act ${!n.summary && n.held_at && n.held_at < nowIso && (n.kind === 'meeting' || n.kind === 'call') ? 'bh-minutes' : ''}`} onClick={() => setModal({ t: 'note', item: n })}>
                                  {!n.summary && n.held_at && n.held_at < nowIso && (n.kind === 'meeting' || n.kind === 'call') ? 'จดสรุป ✎' : 'แก้ / ดูเต็ม'}</button></span>
                              </div>
                              <div className="bh-note-t">{n.title}</div>
                              {n.agenda && <div className="bh-note-x"><b>วาระ:</b> {n.agenda}</div>}
                              {n.summary ? <div className="bh-note-x"><b>สรุป:</b> {n.summary}</div>
                                : (n.kind === 'meeting' || n.kind === 'call') && <div className="bh-note-x muted">ยังไม่ได้ใส่สรุปผล — กด “แก้” หลังคุยจบ</div>}
                              {n.attendees && <div className="bh-note-x muted">ผู้เข้าร่วม: {n.attendees}</div>}
                              {(n.files ?? []).length > 0 && <div className="pa-files">{n.files.map(f => <FileChip key={f.id} f={f} onOpen={openFile}/>)}</div>}
                            </div>
                          </div>
                        );
                      })}
                      {!shownNotes.length && <div className="jr-empty">ยังไม่มีบันทึก — ประชุมครั้งหน้าจดที่นี่ อ่านย้อนได้ทั้งหมด</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── ใบสั่งซื้อ ↔ ของที่มาถึง (deploy-28): สั่งกี่ชิ้น ราคาตรงกับที่ตกลงไม๊ ──
                   ซ่อนจากคลัง: เป็นข้อมูลต้นทุน (RLS can_see_cost) ถ้าโชว์จะเห็นแต่การ์ดว่างกดแล้ว error */}
              {canCost && <PurchaseOrders
                partner={cur}
                consignmentIds={myConsIds}
                shipLabel={consLabel}
                canWrite={canWrite}
                mock={mock}
                onMsg={setActMsg}
                onFlash={say}/>}

              {/* ── เอกสารจากตู้ทั้งหมด: อัลบั้มสะสม ช่องมาตรฐานเหมือนกันทุกตู้ ── */}
              <div className="bh-card bh-wide">
                <div className="bh-card-h">
                  <span className="bh-card-t">เอกสารจากทุกตู้ของ {cur.supplier_name}</span>
                  <span className="bh-card-s">ทุกตู้มีช่องเหมือนกัน — ช่องที่ว่างคือใบที่ยังไม่ได้ · ดึงจาก Console อัตโนมัติ ไม่ต้องอัปซ้ำ</span>
                  {docs.length > 0 && <button className="mini" disabled={zipBusy} onClick={zipAll}>{zipBusy ? 'กำลังรวม…' : `⬇ ZIP ทั้งหมด (${docs.length})`}</button>}
                </div>

                {albums.length > 0 && (
                  <div className="bh-al-bar">
                    <span className="bh-al-score">เก็บครบ <b>{completeN}</b> / {scored.length} ตู้</span>
                    <span className="bh-al-meter"><i style={{ width: `${scored.length ? Math.round(completeN / scored.length * 100) : 0}%` }}/></span>
                    <span className="bh-al-n">ไฟล์ {docs.length} ใบ</span>
                    {gapTotal > 0 && <span className="bh-al-gap">ยังขาด {gapTotal} ช่อง</span>}
                    <span className="bh-al-find">
                      <input value={docQ} onChange={e => setDocQ(e.target.value)}
                        placeholder="ค้นหา — ชื่อไฟล์ · ชื่อเอกสาร · รหัสตู้ · เลข INV"/>
                      {docQ
                        ? <button className="bh-al-x" onClick={() => setDocQ('')} title="ล้างคำค้น">✕</button>
                        : <span className="bh-al-cnt">{docs.length} ใบ</span>}
                    </span>
                    <button className="mini" onClick={() => setOpenShip(Object.fromEntries(albums.map(a => [a.sh.id, true])))}>เปิดทุกตู้</button>
                    <button className="mini" onClick={() => setOpenShip(Object.fromEntries(albums.map(a => [a.sh.id, false])))}>ย่อทุกตู้</button>
                  </div>
                )}

                {docCats.length > 1 && (
                  <div className="catleg bh-filters">
                    <button className={`cl ${docFilter === 'all' ? 'on' : ''}`} onClick={() => setDocFilter('all')}>ทั้งหมด {docs.length}</button>
                    {docCats.map(c => <button key={c} className={`cl ${c} ${docFilter === c ? 'on' : ''}`}
                      onClick={() => setDocFilter(docFilter === c ? 'all' : c)}>{CAT_TH[c] || c} {catCount[c] ?? 0}</button>)}
                  </div>
                )}

                {q && <div className="bh-al-hint">พบ {hitFileN} ใบ ใน {shownAlbums.length} ตู้ · <button className="bh-al-clear" onClick={() => setDocQ('')}>ล้างคำค้น ✕</button></div>}

                {shownAlbums.map(a => {
                  const open = isOpen(a);
                  const showFiles = fileList[a.sh.id];
                  const rows = (showFiles === 'all' ? a.files : a.files.filter(d => showFiles?.includes?.(d.req_type)))
                    .filter(d => docFilter === 'all' || reqCat(d.req_type) === docFilter)
                    .filter(d => !q || a.shipHit || a.hitFiles.includes(d))
                    .sort((x, y) => String(y.uploaded_at || '').localeCompare(String(x.uploaded_at || '')));
                  return (
                    <div className={`bh-album u-${a.vm?.urgency ?? 'ok'} ${open ? 'on' : ''}`} key={a.sh.id}>
                      <button className="bh-al-h" onClick={() => setOpenShip(o => ({ ...o, [a.sh.id]: !open }))}>
                        <span className="dot"/>
                        <span className="bh-al-code">{hiText(a.sh.code, q)}</span>
                        <span className="bh-al-nm">{hiText(a.vm?.displayName ?? '', q)}</span>
                        {a.inv.map(v => <span className="pill" key={v}>INV {hiText(v, q)}</span>)}
                        <span className="bh-al-eta">{a.sh.eta ? clockText(a.sh.eta) : 'ไม่มี ETA'} · {a.vm?.phase}</span>
                        <span className="bh-al-sc"><b>{a.have}/{a.need}</b> ช่อง</span>
                        <span className="bh-al-m"><i style={{ width: `${a.need ? Math.round(a.have / a.need * 100) : 0}%` }}/></span>
                        {a.complete ? <span className="bh-al-done">✓ ครบชุด</span>
                          : a.gaps > 0 ? <span className="bh-al-miss">ขาด {a.gaps} ช่อง</span> : null}
                        <span className="bh-al-car">{open ? '▲' : '▼'}</span>
                      </button>

                      {open && (
                        <div className="bh-al-bd">
                          <div className="bh-slots">
                            {a.slots.map(sl => {
                              const dim = docFilter !== 'all' && sl.cat !== docFilter;
                              const one = sl.files[0];
                              const label = sl.state === 'ok' ? `${sl.files.length} ใบ — กดเพื่อเปิดไฟล์ล่าสุด`
                                : sl.state === 'flag' ? 'ไฟล์มีปัญหา — เปิดตู้เพื่อตรวจ'
                                : sl.state === 'miss' ? 'ยังไม่มี — เติมที่ Console ของตู้นี้'
                                : 'ตู้นี้ไม่ต้องใช้ใบนี้';
                              return (
                                <button key={sl.s} className={`bh-sl st-${sl.state} k-${sl.cat} ${dim ? 'dim' : ''} ${sl.urgent ? 'hot' : ''}`}
                                  disabled={sl.state === 'na'}
                                  title={`${sl.s} — ${label}${sl.files.length ? '\n' + sl.files.map(f => f.file_name).join('\n') : ''}`}
                                  onClick={() => {
                                    if (sl.state === 'na') return;
                                    if (sl.files.length === 1) openFile(one);
                                    else if (sl.files.length > 1) setFileList(f => ({ ...f, [a.sh.id]: sl.t }));
                                    else onOpenShipment(a.sh.code);
                                  }}>
                                  <span className="bh-sl-n">{sl.i + 1}</span>
                                  <span className="bh-sl-t">{sl.s}</span>
                                  <span className="bh-sl-mk">{SLOT_MARK[sl.state]}</span>
                                  {sl.files.length > 1 && <span className="bh-sl-x">×{sl.files.length}</span>}
                                </button>
                              );
                            })}
                          </div>

                          {!a.closed && a.gaps > 0 && (
                            <div className="bh-al-gapline">
                              ยังไม่มี: {a.slots.filter(x => x.state === 'miss').map(x => x.s).join(' · ')}
                              <button className="t-go sm" onClick={() => onOpenShipment(a.sh.code)}>เติมที่ Console <Arrow/></button>
                            </div>
                          )}
                          {!a.closed && a.vm?.why && <div className="bh-al-why">● {a.vm.why}</div>}

                          <div className="bh-al-acts">
                            <button className="bh-al-more" onClick={() => setFileList(f => ({ ...f, [a.sh.id]: showFiles ? null : 'all' }))}>
                              {showFiles ? '▲ ซ่อนชื่อไฟล์' : `▼ ดูชื่อไฟล์ ${a.files.length} ใบ`}
                            </button>
                            {a.extras.length > 0 && !showFiles && <span className="bh-al-extra">+ {a.extras.length} ใบนอกช่องมาตรฐาน</span>}
                            <button className="t-go sm" onClick={() => onOpenShipment(a.sh.code)}>เปิดตู้ <Arrow/></button>
                          </div>

                          {showFiles && (rows.length ? (
                            <div className="bh-led">
                              {rows.map(d => {
                                const [stem, ext] = splitName(d.file_name);
                                return (
                                  <button className="bh-led-r" key={d.id} onClick={() => openFile(d)} title={d.file_name}>
                                    <span className={`bh-led-dot k-${reqCat(d.req_type)}`}/>
                                    <span className="bh-led-t">{hiText(reqLabel(d.req_type), q)}</span>
                                    <span className="bh-led-d">{d.uploaded_at ? fmtD(d.uploaded_at) : ''}</span>
                                    <span className="bh-led-f"><span className="stem">{hiText(stem, q)}</span><span className="ext">{ext}</span></span>
                                    <span className="bh-led-go">เปิด →</span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : <div className="bh-empty">ไม่มีไฟล์ที่ตรงกับตัวกรอง</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {!albums.length && <div className="bh-empty">ยังไม่มีเอกสาร — เมื่ออัปโหลดเอกสารในตู้ของแบรนด์นี้ที่ Console จะมารวมที่นี่เอง</div>}
                {!!albums.length && !shownAlbums.length && (
                  <div className="bh-empty">ไม่พบคำนี้ — ลองพิมพ์รหัสตู้ (เช่น SP-2607) หรือชื่อเอกสาร (เช่น Packing)</div>
                )}
              </div>

              {/* ── ไฟล์ & ลิงก์ของแบรนด์ ── */}
              <div className="bh-card bh-wide">
                <div className="bh-card-h"><span className="bh-card-t">ไฟล์ & ลิงก์ของแบรนด์</span>
                  <span className="bh-card-s">ไฟล์เก็บส่วนตัว (เปิดผ่านลิงก์ปลอดภัยชั่วคราว) · ลิงก์ = ใส่ URL ได้เลย</span></div>
                <div className="bh-assets">
                  {ASSET_KINDS.map(k => {
                    const list = (cur.assets ?? []).filter(a => a.kind === k.k);
                    return (
                      <div className="pa-cat" key={k.k}>
                        <div className="pa-cat-h">
                          <span className="pa-cat-l">{k.label}</span>
                          <span className="grp-actions">
                            <button className="pa-add" disabled={detailBusy} onClick={() => { assetKind.current = k.k; assetFile.current?.click(); }}>＋ แนบไฟล์</button>
                            <button className="pa-add" onClick={() => setModal({ t: 'link', kind: k.k })}>＋ ลิงก์</button>
                          </span>
                        </div>
                        {k.hint && !list.length && <div className="pa-empty">{k.hint}</div>}
                        <div className="pa-files">
                          {list.map(a => <FileChip key={a.id} f={a} onOpen={openFile}
                            onEdit={() => setModal({ t: 'asset', item: a })}
                            onRemove={() => saveAssets(list => list.filter(x => x.id !== a.id)).catch(er => setActMsg(er.message ?? String(er)))}/>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── แผ่นพิมพ์ A4 "สมุดส่งมอบ" — ซ่อนบนจอ โชว์เฉพาะตอนพิมพ์ ── */}
              <div className="bh-sheet">
                <div className="a4">
                  <div className="a4-head">
                    <div>
                      <div className="a4-brand">{DOC_HEAD[0]}</div>
                      <div className="a4-org">{DOC_HEAD[1]}</div>
                      <div className="a4-org">{DOC_HEAD[2]}</div>
                      <div className="a4-tax">สรุปข้อมูลแบรนด์ / Brand Summary Sheet — <b>เอกสารภายใน</b></div>
                    </div>
                    <div className="a4-meta">
                      <div>แบรนด์: <b>{cur.supplier_name}</b></div>
                      {cur.company_name && <div>บริษัท: {cur.company_name}</div>}
                      <div>{cur.country ? countryTh(cur.country) : ''}{cur.handler ? ` · ผู้ดูแล ${cur.handler}` : ''}</div>
                      <div>พิมพ์ {fmtD(new Date().toISOString())} · ข้อมูลครบ {checkN}/{checklist.length}</div>
                      {checkN < checklist.length && <div>ยังขาด: {checklist.filter(c => !c.ok).map(c => c.t).join(', ')}</div>}
                    </div>
                  </div>

                  {cur.brand_profile && <div className="bh-sh-sec"><div className="bh-sh-h">ภาพรวมแบรนด์</div><div className="bh-sh-tx">{cur.brand_profile}</div></div>}

                  <div className="bh-sh-sec">
                    <div className="bh-sh-h">ติดต่อ & เงื่อนไขการสั่งซื้อ</div>
                    {facts.length ? (
                      <table className="a4-table"><tbody>
                        {facts.map(x => <tr key={x.k}><th style={{ width: 170 }}>{x.label}</th><td>{x.type === 'days' ? `${x.v} วัน` : x.v}</td></tr>)}
                      </tbody></table>
                    ) : <div className="bh-sh-tx muted">— ยังไม่ได้กรอก —</div>}
                  </div>

                  <div className="bh-sh-sec">
                    <div className="bh-sh-h">ผู้ติดต่อ ({contacts.length})</div>
                    {contacts.length ? (
                      <table className="a4-table">
                        <thead><tr><th>ชื่อ</th><th>ดูแลเรื่อง</th><th>อีเมล / โทร / แชท</th><th style={{ width: 150 }}>โน้ต</th></tr></thead>
                        <tbody>{contacts.map(c => (
                          <tr key={c.id}>
                            <td>{c.is_primary ? '★ ' : ''}{c.name}</td>
                            <td>{c.role || '—'}{c.languages ? ` (${c.languages})` : ''}</td>
                            <td>{[c.email, c.phone, c.chat_id].filter(Boolean).join(' · ') || '—'}</td>
                            <td>{c.note || ''}</td>
                          </tr>))}
                        </tbody>
                      </table>
                    ) : <div className="bh-sh-tx muted">— ยังไม่มีผู้ติดต่อ —</div>}
                  </div>

                  {(cur.key_facts || cur.quirks || watchouts.length > 0) && (
                    <div className="bh-sh-sec">
                      <div className="bh-sh-h">สิ่งที่ต้องรู้ / ระวัง</div>
                      {cur.key_facts && <div className="bh-sh-tx">{cur.key_facts}</div>}
                      {cur.quirks && <div className="bh-sh-tx"><b>ระวังเป็นพิเศษ:</b> {cur.quirks}</div>}
                      {watchouts.map((w, i) => <div className="bh-sh-tx" key={i}>● {w.text} <i>({w.from_shipment_code})</i></div>)}
                    </div>
                  )}

                  {cycles.length > 0 && (
                    <div className="bh-sh-sec">
                      <div className="bh-sh-h">รอบสั่งซื้อ & วันสำคัญประจำปี</div>
                      <table className="a4-table">
                        <thead><tr><th style={{ width: 110 }}>เมื่อไร</th><th>รอบ</th><th style={{ width: 220 }}>โน้ต</th></tr></thead>
                        <tbody>{cycles.map(row => (
                          <tr key={row.id}><td>{cycleText(row)} ทุกปี</td><td>{row.label}{row.lead_days ? ` (เตือนล่วงหน้า ${row.lead_days} วัน)` : ''}</td><td>{row.note || ''}</td></tr>))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(openRems.length > 0 || soonMeetings.length > 0) && (
                    <div className="bh-sh-sec">
                      <div className="bh-sh-h">งานค้าง & นัดหมายข้างหน้า</div>
                      {openRems.map(r => <div className="bh-sh-tx" key={r.id}>○ {fmtDue(r)} — {r.title}{r.assignee ? ` (${staffName(r.assignee) || 'พนักงาน'})` : ''}</div>)}
                      {soonMeetings.map(n => <div className="bh-sh-tx" key={n.id}>◆ {fmtDT(n.held_at)} — {n.title}</div>)}
                    </div>
                  )}

                  {myVms.length > 0 && (
                    <div className="bh-sh-sec">
                      <div className="bh-sh-h">ตู้ล่าสุด (แสดง {Math.min(10, myVms.length)} จาก {myVms.length})</div>
                      <table className="a4-table">
                        <thead><tr><th style={{ width: 120 }}>รหัสตู้</th><th>ชื่อ</th><th style={{ width: 70 }}>ทาง</th><th style={{ width: 110 }}>ขั้นตอน</th><th style={{ width: 100 }}>ของถึง</th></tr></thead>
                        <tbody>{[...myVms].sort((a, b) => String(b.raw?.eta || '').localeCompare(String(a.raw?.eta || ''))).slice(0, 10).map(v => (
                          <tr key={v.id}><td>{v.id}</td><td>{v.displayName}</td><td>{v.mode === 'air' ? 'AIR' : 'SEA'}</td><td>{v.phase}</td><td>{v.raw?.eta ? fmtYmd(v.raw.eta) : '—'}</td></tr>))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(cur.assets ?? []).length > 0 && (
                    <div className="bh-sh-sec">
                      <div className="bh-sh-h">ไฟล์ & ลิงก์ของแบรนด์</div>
                      {(cur.assets ?? []).map(a => {
                        const st = assetState(a);
                        return <div className="bh-sh-tx" key={a.id}>· {a.name}{a.version_label ? ` (${a.version_label})` : ''}
                          {st ? ` — ใช้ได้ถึง ${fmtYmd(a.valid_to)}${st.level === 'exp' ? ' [หมดอายุแล้ว]' : ''}` : ''}
                          {a.url ? ` — ${a.url}` : ''}</div>;
                      })}
                    </div>
                  )}

                  <div className="a4-sign">
                    <div>ผู้จัดทำ / Prepared by <b>{signer?.full_name || '______________________'}</b>{signer?.job_title ? ` (${signer.job_title})` : ''} วันที่ __________</div>
                    <div>ผู้รับเอกสาร / Received by ______________________ วันที่ __________</div>
                  </div>
                  <div className="a4-foot">เอกสารภายใน — ใช้สำหรับส่งต่องานภายในทีมเท่านั้น · สร้างจาก INBOUNDpoint</div>
                </div>
              </div>

              {/* ── ตู้ของเจ้านี้ ── */}
              <div className="bh-card bh-wide">
                <div className="bh-card-h"><span className="bh-card-t">ตู้ของ {cur.supplier_name} ({myVms.length})</span>
                  <span className="bh-card-s">กำลังทำ {activeVms.length} · เสร็จแล้ว {myVms.length - activeVms.length}</span></div>
                {myVms.length ? (
                  <div className="bh-ships">
                    {[...myVms].sort((a, b) => String(b.raw?.eta || '').localeCompare(String(a.raw?.eta || ''))).map(v => (
                      <div className={`bh-ship u-${v.urgency}`} key={v.id}>
                        <span className="dot"/>
                        <span className="bh-ship-code">{v.id}</span>
                        <span className="bh-ship-name">{v.displayName}</span>
                        <span className={`pill mode ${v.mode}`}>{v.mode === 'air' ? 'AIR' : 'SEA'}</span>
                        <span className="pill">{v.phase}</span>
                        <span className="bh-ship-eta">{v.raw?.eta ? clockText(v.raw.eta) : 'ไม่มี ETA'}</span>
                        <button className="t-go sm" onClick={() => onOpenShipment(v.id)}>เปิดตู้ <Arrow/></button>
                      </div>
                    ))}
                  </div>
                ) : <div className="bh-empty">ยังไม่มีตู้ที่ใช้ชื่อนี้ — ถ้าตู้ใน Console สะกดชื่อ supplier ต่างออกไป ให้เพิ่มเป็น “ชื่อสะกดอื่น” ใน ✎ แก้ข้อมูล</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal?.t === 'partner' && (
        <PartnerModal key={modal.item?.id ?? 'new'} item={modal.item} suggestions={[...unmatched, ...unmatchedAliases]} isAdmin={isAdmin}
          shipCount={modal.item ? (shipsOf[modal.item.id] ?? []).length : 0}
          onClose={() => setModal(null)} onSave={f => savePartner(f, modal.item)} onDelete={deletePartner}/>
      )}
      {modal?.t === 'contact' && cur && (
        <ContactModal key={modal.item?.id ?? 'new'} item={modal.item} onClose={() => setModal(null)}
          onSave={f => saveContact(f, modal.item)} onDelete={deleteContact}/>
      )}
      {modal?.t === 'note' && cur && (
        <NoteModal key={modal.item?.id ?? 'new'} item={modal.item} initialKind={modal.kind}
          hasReminder={!!modal.item && reminders.some(r => r.note_id === modal.item.id && !r.done)}
          canUpload={canWrite || mock} onUpload={uploadFor} onOpenFile={openFile}
          onClose={() => setModal(null)} onSave={(f, o) => saveNote(f, o, modal.item)} onDelete={deleteNote}/>
      )}
      {modal?.t === 'link' && cur && (
        <AssetModal kind={modal.kind} onClose={() => setModal(null)} onSave={saveOneAsset}/>
      )}
      {modal?.t === 'asset' && cur && (
        <AssetModal key={modal.item.id} item={modal.item} onClose={() => setModal(null)} onSave={saveOneAsset}/>
      )}
    </div>
  );
}
